import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { parsePhoneNumber } from "libphonenumber-js";
import { API_BASE_URL } from "../../Store/apiStore";
// import { useNavigate } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import decodeToken from "../../lib/decodeToken";
import {
  listBlacklist as fetchBlacklist,
  listContacts as fetchContacts,
  getAgentCategories,
  getIndustryTemplates,
  updateAgentCategories,
  listAgentContacts as fetchAgentContacts,
  agentContactsSummary as fetchAgentContactsSummary,
  blockAgentContact as apiBlockAgentContact,
  unblockAgentContact as apiUnblockAgentContact,
  promoteAgentContact as apiPromoteAgentContact,
  getPromoteInfo as apiGetPromoteInfo,
  unblockAllAgents as apiUnblockAllAgents,
  bulkBlockAgentContacts as apiBulkBlock,
  bulkUnblockAgentContacts as apiBulkUnblock,
  bulkPromoteAgentContacts as apiBulkPromote,
  deleteAgentContact as apiDeleteAgentContact,
  bulkDeleteAgentContacts as apiBulkDeleteAgentContacts,
  bulkUpdateContacts as apiBulkUpdateContacts,
} from "../../Store/apiStore";
import { useContactStore } from "../../Store/contactStore";
import styles from "./Contactmanager.module.css";
import NewModal from "../NewModal/NewModal";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import { FixedSizeList } from "react-window";
// ADD after existing imports (top of file):
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { trackEvent } from "../../lib/analytics";
import { isIndiaSite } from "../../utils/apiBackend";
// ── Constants ─────────────────────────────────────────────────────────────────
// Module-scope stable empty arrays. Used as default prop values so a
// caller that omits `contacts`/`blacklist`/`agents` doesn't accidentally
// trigger the "ref changed" useEffect on every render — the previous
// inline `= []` defaults created a fresh array reference each render,
// which loop'd setContacts(...) → store update → re-render → loop.
// Stable refs make those guards work as intended.
const STABLE_EMPTY_CONTACTS = [];
const STABLE_EMPTY_BLACKLIST = [];
const STABLE_EMPTY_AGENTS = [];

// Phone inputs ka default country. Pehle teeno jagah "us" hardcoded tha, is
// liye India site pe bhi Add/Edit Contact +1 se khulta tha aur har number
// galat country code ke saath type hota tha. Host session me badalta nahi, is
// liye ek hi baar module load pe nikal lete hain.
const DEFAULT_PHONE_COUNTRY = isIndiaSite() ? "in" : "us";

const CATEGORIES = ["all", "family", "friends", "colleagues", "customer", "other"];
const FILTER_OPTIONS = [
  { key: "vip", label: "VIP Contact" },
  { key: "nonVip", label: "Non-VIP Contact" },
  { key: "blocked", label: "Blocked Contact" },
  { key: "nonBlocked", label: "Non-Blocked Contact" },
];


const formatLocalDT = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return (
      d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) +
      ", " +
      d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true })
    );
  } catch {
    return "";
  }
};

const CATEGORY_COLORS = {
  family: { bg: "#FFF0F0", color: "#D63B3B" },
  friends: { bg: "#F0FFF4", color: "#2D7A4F" },
  colleagues: { bg: "#EEF2FF", color: "#4338CA" },
  customer: { bg: "#FFF7ED", color: "#C05621" },
  other: { bg: "#F5F5F5", color: "#555555" },
};

const WINDOW_OPTIONS = [
  { label: "12 hrs", value: 12 },
  { label: "24 hrs", value: 24 },
  { label: "48 hrs", value: 48 },
  { label: "72 hrs", value: 72 },
];

// ── Detect user's country from browser/timezone — fallback US ────────────────
const _detectedCountry = (() => {
  try {
    // 1. navigator.language → "en-IN" → "IN"
    const lang = navigator.language || navigator.userLanguage || "";
    const parts = lang.split("-");
    if (parts.length >= 2 && parts[1].length === 2) return parts[1].toUpperCase();

    // 2. Intl timezone → rough country mapping
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.startsWith("Asia/Kolkata") || tz.startsWith("Asia/Calcutta")) return "IN";
    if (tz.startsWith("America/")) return "US";
    if (tz.startsWith("Europe/London")) return "GB";
    if (tz.startsWith("Asia/Dubai")) return "AE";
    if (tz.startsWith("Asia/Muscat")) return "OM";
  } catch { }
  return "US"; // default fallback
})();

// ── Phone validation ──────────────────────────────────────────────────────────
function validatePhone(raw) {
  if (!raw || raw === "+") return { valid: false, e164: null, display: raw, country: null };
  try {
    // 1. Already has + prefix — parse directly
    if (raw.startsWith("+")) {
      const parsed = parsePhoneNumber(raw);
      if (parsed && parsed.isValid()) {
        return {
          valid: true,
          e164: parsed.format("E.164"),
          display: parsed.formatInternational(),
          country: parsed.country,
        };
      }
    }

    // 2. No + prefix — try with auto-detected country first
    const digits = raw.replace(/\D/g, "");
    if (digits.length > 0) {
      try {
        const parsed = parsePhoneNumber(digits, _detectedCountry);
        if (parsed && parsed.isValid()) {
          return {
            valid: true,
            e164: parsed.format("E.164"),
            display: parsed.formatInternational(),
            country: parsed.country,
          };
        }
      } catch { }

      // 3. Fallback — try US (+1) for 10-digit numbers
      if (digits.length === 10) {
        const parsed = parsePhoneNumber("+1" + digits);
        if (parsed && parsed.isValid()) {
          return { valid: true, e164: parsed.format("E.164"), display: parsed.formatInternational(), country: "US" };
        }
      }
      if (digits.length === 11 && digits.startsWith("1")) {
        const parsed = parsePhoneNumber("+" + digits);
        if (parsed && parsed.isValid()) {
          return { valid: true, e164: parsed.format("E.164"), display: parsed.formatInternational(), country: "US" };
        }
      }
    }

    return { valid: false, e164: null, display: raw, country: null };
  } catch {
    return { valid: false, e164: null, display: raw, country: null };
  }
}

// ── Phone input helpers ───────────────────────────────────────────────────────
const phoneOnFocus = (value, setter) => {
  if (!value) setter("+1");
};

const phoneOnChange = (raw, setter, extraSetter) => {
  let val = raw.replace(/[^\d+]/g, ""); // ADD THIS LINE
  if (val && !val.startsWith("+")) val = "+" + val;
  if (val === "") val = "";
  setter(val);
  if (extraSetter) extraSetter("");
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  const iconRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (show && iconRef.current && tooltipRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const tip = tooltipRef.current;
      tip.style.position = "fixed";
      tip.style.top = (rect.top - 8) + "px";
      tip.style.left = (rect.left + rect.width / 2) + "px";
      tip.style.transform = "translate(-50%, -100%)";
    }
  }, [show]);

  return (
    <span className={styles.infoWrap}>
      <span
        ref={iconRef}
        className={styles.iIcon}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow((p) => !p)}
      >
        <img src="/svg/informtion-icon 2.svg" alt="informtion-icon 2.svg" />
      </span>
      {show && createPortal(
        <span ref={tooltipRef} className={styles.tooltip}>
          {text}
        </span>,
        document.body
      )}
    </span>
  );
};

const BADGE_PALETTE = [
  { bg: "#FFF0F0", color: "#D63B3B" },
  { bg: "#F0FFF4", color: "#2D7A4F" },
  { bg: "#EEF2FF", color: "#4338CA" },
  { bg: "#FFF7ED", color: "#C05621" },
  { bg: "#FEF3C7", color: "#92400E" },
  { bg: "#F0F9FF", color: "#0369a1" },
  { bg: "#FDF2F8", color: "#BE185D" },
  { bg: "#ECFDF5", color: "#047857" },
];

const CategoryBadge = ({ cat }) => {
  const known = CATEGORY_COLORS[cat];
  if (known)
    return (
      <span className={styles.badge} style={{ background: known.bg, color: known.color }}>
        {(cat || "other").replace(/_/g, " ")}
      </span>
    );
  const hash = (cat || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const p = BADGE_PALETTE[hash % BADGE_PALETTE.length];
  return (
    <span className={styles.badge} style={{ background: p.bg, color: p.color }}>
      {(cat || "other").replace(/_/g, " ")}
    </span>
  );
};

const Avatar = ({ name, size = 40 }) => {
  const firstChar = (name || "").trim()[0] || "";
  const isLetter = /^[a-zA-Z]$/.test(firstChar);

  if (!isLetter) {
    return (
      <div className={styles.avatar} style={{ width: size, height: size, background: "#EFE9FF" }}>
        <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="8" r="4" stroke="#9B7FE0" strokeWidth="2" />
          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#9B7FE0" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
    );
  }

  return (
    <div className={styles.avatar} style={{ width: size, height: size, background: "#EFE9FF" }}>
      {firstChar.toUpperCase()}
    </div>
  );
};

const CustomCheckbox = ({ checked }) => (
  <span style={{
    width: 18, height: 18, borderRadius: 5, flexShrink: 0,
    border: checked ? "none" : "1.5px solid #d0d0d8",
    background: checked ? "#6524eb" : "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s",
  }}>
    {checked && (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </span>
);
// ── Confirm Dialog ────────────────────────────────────────────────────────────
// Uses NewModal — children contain the message + action buttons
const ConfirmDialog = ({
  isOpen,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmDanger = false,
  onCancel,
  onConfirm,
}) => (
  <NewModal isOpen={isOpen} onClose={onCancel} title={title} height="auto" maxHeight="60vh">
    <p style={{ margin: "0 0 20px", fontSize: 14, color: "#444", lineHeight: 1.5 }}>{message}</p>
    <div style={{ display: "flex", gap: 10 }}>
      <button
        onClick={onCancel}
        style={{
          flex: 1,
          padding: "11px 0",
          background: "#f5f5f5",
          border: "none",
          borderRadius: 10,
          fontSize: 14,
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        {cancelLabel}
      </button>
      <button
        onClick={onConfirm}
        style={{
          flex: 1,
          padding: "11px 0",
          border: "none",
          borderRadius: 10,
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer",
          background: confirmDanger ? "#D63B3B" : "#6524eb",
          color: "#fff",
        }}
      >
        {confirmLabel}
      </button>
    </div>
  </NewModal>
);

// ── Agent Picker Dialog ───────────────────────────────────────────────────────
const AgentPickerDialog = ({ isOpen, agents, onClose, onConfirm }) => {
  const [selected, setSelected] = useState([]);

  const toggle = (agentId) =>
    setSelected((prev) =>
      prev.includes(agentId) ? prev.filter((id) => id !== agentId) : [...prev, agentId]
    );

  return (
    <NewModal isOpen={isOpen} onClose={onClose} title="Enable Auto Spam Blocking" height="auto" maxHeight="80vh">
      <p style={{ fontSize: 14, color: "#555", marginBottom: 16, lineHeight: 1.5 }}>
        Select which agent(s) to enable spam blocking for.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
        {agents.map((a) => (
          <label
            key={a.agent_id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 14px",
              borderRadius: 10,
              border: `1.5px solid ${selected.includes(a.agent_id) ? "#6524eb" : "#eee"}`,
              background: selected.includes(a.agent_id) ? "#f5f0ff" : "#fff",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            <input
              type="checkbox"
              checked={selected.includes(a.agent_id)}
              onChange={() => toggle(a.agent_id)}
              style={{ accentColor: "#6524eb", width: 16, height: 16 }}
            />
            <span style={{ fontSize: 14, fontWeight: 500, color: "#222" }}>{a.agentName}</span>
          </label>
        ))}
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button
          onClick={onClose}
          style={{
            flex: 1,
            padding: "11px 0",
            background: "#f5f5f5",
            border: "none",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          disabled={selected.length === 0}
          onClick={() => {
            onConfirm(selected);
            onClose();
          }}
          style={{
            flex: 1,
            padding: "11px 0",
            border: "none",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            cursor: selected.length === 0 ? "not-allowed" : "pointer",
            background: "#6524eb",
            color: "#fff",
            opacity: selected.length === 0 ? 0.5 : 1,
          }}
        >
          Enable ({selected.length})
        </button>
      </div>
    </NewModal>
  );
};

// ── Forward Phone Modal ───────────────────────────────────────────────────────
const ForwardPhoneModal = ({ isOpen, currentPhone, onClose, onSave }) => {
  // const [phone, setPhone] = useState(currentPhone || "1");

  // const phoneVal = validatePhone(phone);
  // const showHint = phone && phone !== "+";
  // const canSave = phoneVal.valid;

  // const handleSave = () => {
  //   if (!canSave) return;
  //   onSave(phoneVal.e164 || phone.trim());
  //   onClose();
  // };

  const [phone, setPhone] = useState(() => {
    const p = currentPhone || "";
    return p.startsWith("+") ? p.slice(1) : p || "1";
  });
  const phoneVal = validatePhone("+" + phone);
  const canSave = phoneVal.valid;

  const handleSave = () => {
    if (!canSave) return;
    onSave(phoneVal.e164 || ("+" + phone));
    onClose();
  };

  return (
    <NewModal isOpen={isOpen} onClose={onClose} title="Set Forward Number" height="auto" maxHeight="70vh">
      <div className={`${styles.formField} ${styles.phoneField}`} style={{ marginBottom: 8 }}>
        {/* <label>Your Phone Number</label>
        <input
          value={phone}
          onFocus={() => phoneOnFocus(phone, setPhone)}
          onChange={(e) => phoneOnChange(e.target.value, setPhone)}
          placeholder="+1 (555) 000-0000"
          className={styles.formInput}
          style={
            showHint && !phoneVal.valid
              ? { borderColor: "#C05621" }
              : showHint && phoneVal.valid
                ? { borderColor: "#2D7A4F" }
                : {}
          }
          autoFocus
        />
        {showHint &&
          (phoneVal.valid ? (
            <p className={styles.fieldOkText}>
              ✓ {phoneVal.display} ({phoneVal.country})
            </p>
          ) : (
            <p className={styles.fieldError}>
              Enter a valid international number — e.g. +1 555 000 0000
            </p>
          ))} */}
        <label>Your Phone Number</label>
        <PhoneInput
          country={DEFAULT_PHONE_COUNTRY}
          value={phone}
          onChange={(val) => setPhone(val)}
          inputClass={styles.formInput}
          containerStyle={{ width: "100%" }}
          inputStyle={{ width: "100%" }}
          enableSearch
        />
        {phone && phone !== "1" && (
          phoneVal.valid ? (
            <p className={styles.fieldOkText}>✓ {phoneVal.display} ({phoneVal.country})</p>
          ) : (
            <p className={styles.fieldError}>Enter a valid international number — e.g. +1 555 000 0000</p>
          )
        )}
      </div>

      <p className={styles.hintText} style={{ marginBottom: 20 }}>
        This number is private — callers will not see it. Only used to forward known contacts.
      </p>

      <div>
        <AnimatedButton
          label="Save & Enable"
          positionType="relative"
          width="100%"
          bottom="0px"
          onClick={handleSave}
          disabled={!canSave}
        />
      </div>
    </NewModal>
  );
};

// ── Agent Settings Sheet ──────────────────────────────────────────────────────
const AgentSettingsSheet = ({ isOpen, agents, onClose, onSave, onLoad, returnTo }) => {
  const [selectedAgent, setSelectedAgent] = useState(agents[0]?.agent_id || "");
  const [settings, setSettings] = useState({
    autoBlacklistEnabled: true,
    maxCallsPerWindow: 3,
    windowHours: 24,
    notifyInApp: true,
    notifyPush: false,
    notifyEmail: false,
    notifySms: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPushConfirm, setShowPushConfirm] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const currentReturnTo = returnTo || `${location.pathname}${location.search}`;

  useEffect(() => {
    if (!selectedAgent || !isOpen) return;
    setLoading(true);
    const load = async () => {
      try {
        const data = onLoad ? await onLoad(selectedAgent) : null;
        if (data && Object.keys(data).length > 0) {
          setSettings((prev) => ({ ...prev, ...data }));
        }
      } catch (e) {
        console.error("[AgentSettingsSheet] load error:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedAgent, isOpen]);

  // Auto-save: update state + fire API call on every change
  const set = (key, val) => {
    const updated = { ...settings, [key]: val };
    setSettings(updated);
    // Auto-save to backend (fire-and-forget, debounced via closure)
    if (selectedAgent && onSave) {
      onSave(selectedAgent, updated).catch((e) => console.error("[AgentSettings] auto-save error:", e));
    }
  };

  // Manual save kept but commented out — uncomment if explicit save button needed later
  // const handleSave = async () => {
  //   setSaving(true);
  //   try {
  //     await onSave?.(selectedAgent, { ...settings });
  //     onClose();
  //   } catch (e) {
  //     console.error("[AgentSettings] save error:", e);
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  const ToggleRow = ({ label, info, field }) => (
    <div className={styles.settingRow}>
      <div className={styles.settingLabel}>
        <span>{label}</span>
        {info && <InfoTooltip text={info} />}
      </div>
      <div
        className={`${styles.toggle} ${settings[field] ? styles.toggleOn : ""}`}
        onClick={async () => {
          if (field === "notifyPush" && !settings.notifyPush) {
            try {
              const token = localStorage.getItem("token");
              if (token) {
                const userObj = decodeToken(token);
                const userId = userObj?.id || userObj?._id;
                if (userId) {
                  const res = await fetch(`${API_BASE_URL}/push/status/${userId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (res.ok) {
                    const data = await res.json();
                    if (!data.pushEnabled) {
                      setShowPushConfirm(true);
                      return;
                    }
                  } else {
                    console.warn("Failed to check push status:", res.status);
                    return;
                  }
                }
              }
            } catch (e) {
              console.error("Failed to check master push status:", e);
              return;
            }
          }
          set(field, !settings[field]);
        }}
      >
        <div className={styles.toggleThumb} />
      </div>
    </div>
  );

  return (
    <NewModal isOpen={isOpen} onClose={onClose} title="Agent Spam Settings" height="auto" maxHeight="90vh">
      <div className={styles.sheetSection}>
        <label className={styles.sheetLabel}>Select Agent</label>
        <select
          value={selectedAgent}
          onChange={(e) => setSelectedAgent(e.target.value)}
          className={styles.sheetSelect}
        >
          {agents.map((a) => (
            <option key={a.agent_id} value={a.agent_id}>
              {a.agentName}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className={styles.sheetLoading}>
          <div className={styles.spinner} />
          <span>Loading settings...</span>
        </div>
      ) : (
        <>
          <div className={styles.sheetSection}>
            <ToggleRow
              label="Auto Spam Blocking"
              info="Automatically block callers classified as spam by the AI agent."
              field="autoBlacklistEnabled"
            />
          </div>

          <div className={`${styles.sheetSection} ${!settings.autoBlacklistEnabled ? styles.dimmed : ""}`}>
            <div className={styles.sliderHeader}>
              <label className={styles.sheetLabel}>Block after irrelevant calls</label>
              <span className={styles.sliderValue}>{settings.maxCallsPerWindow} calls</span>
            </div>
            <input
              type="range"
              min="1"
              max="10"
              step="1"
              value={settings.maxCallsPerWindow}
              disabled={!settings.autoBlacklistEnabled}
              onChange={(e) => set("maxCallsPerWindow", Number(e.target.value))}
              className={styles.slider}
            />
            <div className={styles.sliderTicks}>
              <span>1</span>
              <span>5</span>
              <span>10</span>
            </div>
          </div>

          <div className={`${styles.sheetSection} ${!settings.autoBlacklistEnabled ? styles.dimmed : ""}`}>
            <p className={styles.sheetSectionTitle}>Notifications when blocked</p>
            <ToggleRow label="In-app" info="Show alert in your dashboard." field="notifyInApp" />
            <ToggleRow label="Push" info="Mobile push notification." field="notifyPush" />
            <ToggleRow label="Email" info="Send email alert (can be noisy at scale)." field="notifyEmail" />
            {/* <ToggleRow label="SMS" info="Send SMS alert (carrier charges apply)." field="notifySms" /> */}
          </div>

          {/* Auto-save enabled — changes save instantly on toggle/slider change */}
          <div style={{ paddingTop: 16, textAlign: "center", fontSize: 12, color: "#888" }}>
            Changes are saved automatically
          </div>
          {/* Uncomment below to restore explicit Save button:
          <div style={{ paddingTop: 20 }}>
            <AnimatedButton
              label="Save Settings"
              isLoading={saving}
              disabled={saving}
              onClick={handleSave}
              positionType="relative"
              width="100%"
            />
          </div>
          */}
        </>
      )}

      {/* Push confirm nested inside AgentSettingsSheet modal */}
      <ConfirmDialog
        isOpen={showPushConfirm}
        title="Master Push Disabled"
        message="Your Master Push Notifications are currently turned OFF. You must enable them in your Profile first."
        confirmLabel="Go to Profile"
        onCancel={() => setShowPushConfirm(false)}
        onConfirm={() => {
          // Remember contact notification settings 
          try {
            sessionStorage.setItem(
              "pendingAgentPush",
              JSON.stringify({ agentId: selectedAgent })
            );
          } catch (_e) { }
          setShowPushConfirm(false);
          onClose();

          navigate("/edit-profile", {
            state: {
              returnTo: currentReturnTo,
            },
          });
        }}
      />
    </NewModal>
  );
};

/*
 * How many per-row rejection reasons to surface when a sync fails. The server
 * returns one error entry per rejected contact, so a whole-address-book failure
 * comes back with hundreds — enough to identify the cause, not enough to bury
 * the dialog.
 */
const MAX_SYNC_ERROR_LINES = 5;

// ── Sync Dialog (Google + Phone only — no CSV/VCF) ───────────────────────────
// Exported — the dialer's Contacts tab reuses this exact sheet so both modules
// sync contacts through the same flow.
export const SyncDialog = ({ isOpen, onClose, onSync, onTriggerGoogleSync, onContactsSynced, oauthReturnTo }) => {
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [syncError, setSyncError] = useState("");

  useEffect(() => {
    const handleRNMessage = (event) => {
      try {
        const msg = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (!msg?.__fromRN) return;

        if (msg.type === "CONTACTS_SYNC_START") {
          setSyncing(true);
          setSyncMsg("Starting sync...");
          setSyncError("");
        }

        if (msg.type === "CONTACTS_PERMISSION_CHECK") {
          setSyncing(true);
          setSyncMsg("Checking contacts permission...");
          setSyncError("");
        }

        if (msg.type === "CONTACTS_API_START") {
          setSyncing(true);
          setSyncMsg(`Syncing ${msg.data?.count ?? ""} contacts...`);
        }

        if (msg.type === "CONTACTS_API_SUCCESS" || msg.type === "CONTACTS_SYNCED") {
          /*
           * Read the server's result instead of assuming the upload landed.
           *
           * `CONTACTS_API_SUCCESS` only means the native shell got HTTP 200.
           * It does NOT mean rows were written: upsertContacts
           * (services/Contactsyncservice.js) drops any contact whose
           * `raw.phone` fails normalizeE164 and returns
           * { inserted, updated, failed, errors } with a 200. A batch where
           * every row is rejected therefore came back as 200 + failed:N, and
           * this branch reported it as a clean success and closed the dialog —
           * the user saw "Syncing 1006 contacts" then "done", with nothing in
           * the list.
           *
           * The browser-picker path below already reads these counts; the two
           * paths hit the SAME server function and must not disagree about
           * what its reply means.
           *
           * Back-compatible: an older native build that sends no `result`
           * yields all-zero counts, `failed > 0` is false, and the success
           * branch runs exactly as before.
           */
          const result = msg.data?.result || {};
          const inserted = result.inserted ?? 0;
          const updated = result.updated ?? 0;
          const failed = result.failed ?? 0;
          // Capped — a whole-address-book failure returns one error per row.
          const errorLines = (result.errors || [])
            .slice(0, MAX_SYNC_ERROR_LINES)
            .map((e) => `${e.raw ?? "(no phone)"}: ${e.reason}`)
            .join("\n");

          setSyncing(false);

          if (failed > 0 && inserted === 0 && updated === 0) {
            // Nothing landed. Keep the dialog open and say so.
            setSyncMsg("");
            setSyncError(
              `All ${failed} contacts failed to sync.` +
              (errorLines ? `\n${errorLines}` : "")
            );
            return;
          }

          if (failed > 0) {
            // Partial — stay open so the rejected rows stay readable.
            setSyncMsg(
              `${inserted} new, ${updated} updated, ${failed} failed`
            );
            setSyncError(errorLines);
            onContactsSynced?.(result);
            return;
          }

          /*
           * Verify against the server before declaring success.
           *
           * Everything above trusts what the native shell tells us. That is not
           * enough: when the shell forwards no `result` object at all, every
           * count reads 0, `failed > 0` is false, and this branch reported a
           * clean success for a sync that wrote nothing — the exact "Syncing
           * 1006 contacts → contacts synced → table still empty" report.
           *
           * So ask the server directly, over the WEB's own base URL and token —
           * the same backend and account the user inspects. `category` maps to
           * the backend's `source` filter, and syncNativeContacts stamps every
           * row it writes with source 'native_app' (it overwrites whatever the
           * client sent), so a non-zero count here is proof rows landed.
           *
           * Only a POSITIVE zero is treated as failure. If either request errors
           * we fall through to success rather than accusing a sync that may well
           * have worked.
           */
          setSyncMsg("Checking your contacts…");
          (async () => {
            const [allRes, nativeRes] = await Promise.all([
              fetchContacts({ page: 1, limit: 1 }).catch(() => null),
              fetchContacts({ page: 1, limit: 1, category: "native_app" }).catch(() => null),
            ]);
            const serverTotal = allRes?.total ?? null;
            const nativeTotal = nativeRes?.total ?? null;
            setSyncing(false);
            setSyncMsg("");

            if (nativeTotal === 0) {
              // Raw payload included deliberately: this is the one moment we
              // need to see what the shell actually sent. Capped so a
              // per-row error list cannot fill the screen.
              let rawPayload = "";
              try {
                rawPayload = JSON.stringify(msg.data ?? {}).slice(0, 300);
              } catch (_e) {
                rawPayload = "(unserialisable)";
              }
              setSyncError(
                "The app reported a successful sync, but the server has 0 phone-synced contacts for this account.\n" +
                `Contacts in account: ${serverTotal ?? "unknown"}\n` +
                `App reported: ${rawPayload}`
              );
              return;
            }

            onContactsSynced?.(result);
            onClose();
          })();
          return;
        }

        // Google OAuth — RN deep link se aaya result
        if (msg.type === "GOOGLE_AUTH_SUCCESS") {
          // Google Contacts authorised in the native app: the shell posts this
          // after the com.rexptin://contacts deep link. Same event as the web
          // ?google_sync=success handlers; the once-per-user guard dedupes.
          trackEvent("google_contacts_connected", { source: "native_app" });
          setSyncMsg("Syncing Google Contacts...");
          (async () => {
            try {
              await onTriggerGoogleSync?.();
              setSyncing(false);
              setSyncMsg("");
              onContactsSynced?.({});
              onClose();
            } catch {
              setSyncing(false);
              setSyncMsg("");
              setSyncError("Google sync failed. Please try again.");
            }
          })();
        }

        if (msg.type === "GOOGLE_AUTH_ERROR" || msg.type === "GOOGLE_AUTH_CANCELLED") {
          setSyncing(false);
          setSyncMsg("");
          setSyncError(msg.data?.message || "Google login cancelled or failed.");
        }

        if (
          msg.type === "CONTACTS_API_ERROR" ||
          msg.type === "CONTACTS_API_FAIL" ||
          msg.type === "CONTACTS_PERMISSION_DENIED" ||
          msg.type === "CONTACTS_ERROR"
        ) {
          setSyncing(false);
          setSyncMsg("");
          setSyncError(msg.data?.message || "Sync failed. Please try again.");
        }
      } catch (e) {
        console.error("[SyncDialog] RN message parse error:", e);
      }
    };

    window.addEventListener("message", handleRNMessage);
    document.addEventListener("message", handleRNMessage);

    return () => {
      window.removeEventListener("message", handleRNMessage);
      document.removeEventListener("message", handleRNMessage);
    };
  }, []);

  // Web: jab user Google OAuth se wapas aaye (back/tab switch), loading reset karo
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && syncing) {
        setSyncing(false);
        setSyncMsg("");
      }
    };
    // pageshow handles bfcache (back button) restore
    const handlePageShow = (e) => {
      if (e.persisted && syncing) {
        setSyncing(false);
        setSyncMsg("");
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [syncing]);
  const handleGoogleClick = async () => {
    setSyncing(true);
    setSyncMsg("Checking Google connection...");
    setSyncError("");
    try {
      const { getGoogleSyncStatus } = await import("../../Store/apiStore");

      // ✅ pehle check karo RN hai ya nahi
      const isRN = !!window.ReactNativeWebView;

      // ✅ isMobile + flow dono pass karo
      const status = await getGoogleSyncStatus({ isMobile: isRN, flow: "contacts", returnTo: oauthReturnTo || undefined });

      if (status.hasToken) {
        // Already connected — seedha sync
        setSyncMsg("Syncing Google Contacts...");
        try {
          await onTriggerGoogleSync?.();
          setSyncing(false);
          onContactsSynced?.({});
          onClose();
        } catch (err) {
          console.error("[SyncDialog] Google sync error:", err);
          setSyncing(false);
          setSyncError("Google sync failed. Please try again.");
        }

      } else if (status.authUrl) {
        if (isRN) {
          // ✅ Mobile: RN ko message bhejo — wo browser mein kholega
          // Google OAuth → backend → com.rexptin://contacts?google_sync=success
          // RN deep link → WebView ko GOOGLE_AUTH_SUCCESS bhejega
          // flow:"contacts" tells App.tsx to use redirectUrl=
          // com.rexptin://contacts on InAppBrowser.openAuth, so the
          // browser auto-closes when the OAuth callback redirects.
          setSyncMsg("Opening Google login...");
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: "OPEN_GOOGLE_AUTH",
            authUrl: status.authUrl,
            flow: "contacts",
          }));
          // Loading state rakho — RN se GOOGLE_AUTH_SUCCESS aane tak
        } else {
          // Web: direct redirect
          setSyncMsg("Redirecting to Google...");
          window.location.href = status.authUrl;
        }

      } else {
        setSyncError("Could not connect to Google. Please try again.");
        setSyncing(false);
      }
    } catch (err) {
      console.error("[SyncDialog] status check error:", err);
      setSyncError("Connection check failed. Please try again.");
      setSyncing(false);
    }
  };
  const handleNativeClick = async () => {
    // 1. React Native WebView — ask native app for contacts
    if (window.ReactNativeWebView) {
      // Show loading state IMMEDIATELY — and keep the dialog open so
      // the user sees the progress copy (Checking permission → Syncing
      // N contacts → Done). Earlier we closed the dialog right away,
      // which left the user staring at a frozen page until the WebView
      // got reloaded. The listener above closes the dialog on
      // CONTACTS_API_SUCCESS / CONTACTS_SYNCED.
      setSyncing(true);
      setSyncMsg("Opening your contacts…");
      setSyncError("");
      const token = localStorage.getItem('token');
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: "REQUEST_CONTACTS",
        token: token
      }));
      return;
    }

    // 2. Browser Contact Picker API (Chrome mobile + HTTPS)
    if ("contacts" in navigator && "ContactsManager" in window) {
      try {
        const props = ["name", "tel", "email"];
        const opts = { multiple: true };
        const contacts = await navigator.contacts.select(props, opts);
        if (contacts && contacts.length > 0) {
          setSyncing(true);
          setSyncMsg("Syncing " + contacts.length + " contacts...");
          setSyncError("");

          // Format & validate phone numbers using existing validatePhone
          const formatted = contacts.map((c) => {
            const rawPhone = c.tel?.[0] || "";
            const validated = validatePhone(rawPhone);
            return {
              name: c.name?.[0] || "",
              phone: validated.e164 || rawPhone,
              email: c.email?.[0] || "",
              source: "phone_picker",
              _valid: validated.valid,
              _raw: rawPhone,
            };
          });

          try {
            const res = await onSync?.("phone_picker", formatted);
            const inserted = res?.inserted ?? 0;
            const failed = res?.failed ?? 0;
            const errors = res?.errors || [];
            setSyncing(false);

            if (failed > 0 && inserted > 0) {
              setSyncMsg(`${inserted} contacts synced, ${failed} failed`);
              setSyncError(errors.map(e => `${e.raw || "Unknown"}: ${e.reason}`).join("\n"));
            } else if (failed > 0 && inserted === 0) {
              setSyncMsg("");
              setSyncError(`All ${failed} contacts failed to sync.\n` + errors.map(e => `${e.raw || "Unknown"}: ${e.reason}`).join("\n"));
            } else {
              const updated = res?.updated ?? 0;
              const msg = updated > 0
                ? `${inserted} new, ${updated} already existed (updated)`
                : `${inserted} contacts synced successfully!`;
              setSyncMsg(msg);
              onContactsSynced?.({});
              setTimeout(() => onClose(), 2000);
            }
          } catch (err) {
            setSyncing(false);
            setSyncMsg("");
            setSyncError("Sync failed: " + (err?.message || "Unknown error"));
          }
        }
      } catch (e) {
        if (e.name !== "TypeError") {
          console.error("[ContactPicker] Error:", e);
          setSyncError("Failed to pick contacts. Please try again.");
        }
      }
      return;
    }

    // 3. Fallback — shouldn't reach here (button hidden on desktop)
    onClose();
  };


  const isNativeApp = !!window.ReactNativeWebView;

  const options = [
    {
      id: "google",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
      ),
      label: "Google Contacts",
      desc: "Sync from your Google account",
      action: handleGoogleClick,
    },
    // Phone Contacts — available only inside the native app
    ...(isNativeApp ? [{
      id: "native",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <rect x="5" y="2" width="14" height="20" rx="2" stroke="#6524eb" strokeWidth="2" fill="none" />
          <circle cx="12" cy="17" r="1" fill="#6524eb" />
        </svg>
      ),
      label: "Phone Contacts",
      desc: "Import from your device",
      action: handleNativeClick,
    }] : []),
  ];

  if (!isOpen) return null;

  return (
    <div className={styles.backdrop} data-sync-dialog-open="true" onClick={() => { setSyncing(false); setSyncMsg(""); onClose(); }}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()}>
        <div className={styles.dialogHeader}>
          <h3>Sync Contacts</h3>
          <button className={styles.closeBtn} onClick={() => { setSyncing(false); setSyncMsg(""); onClose(); }}>✕</button>
        </div>
        <p className={styles.dialogSub}>Choose how to sync your contacts</p>

        {syncing && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "#EEF2FF", borderRadius: 10, margin: "8px 0 4px", fontSize: 13, color: "#4338CA", fontWeight: 500 }}>
            <div className={styles.spinner} style={{ width: 16, height: 16, borderWidth: 2 }} />
            {syncMsg || "Please wait..."}
          </div>
        )}
        {!syncing && syncMsg && !syncError && (
          <div style={{ padding: "10px 14px", background: "#F0FFF4", borderRadius: 10, margin: "8px 0 4px", fontSize: 13, color: "#2D7A4F", fontWeight: 500 }}>
            {syncMsg}
          </div>
        )}
        {!syncing && syncMsg && syncError && (
          <div style={{ padding: "10px 14px", background: "#FFF7ED", borderRadius: 10, margin: "8px 0 4px", fontSize: 13, color: "#C05621", fontWeight: 500 }}>
            {syncMsg}
          </div>
        )}
        {syncError && (
          <div style={{ padding: "10px 14px", background: "#FFF0F0", borderRadius: 10, margin: "8px 0 4px", fontSize: 13, color: "#D63B3B", fontWeight: 500, whiteSpace: "pre-line" }}>
            {syncError}
          </div>
        )}

        <div className={styles.syncOptions} style={syncing ? { opacity: 0.5, pointerEvents: "none" } : {}}>
          {options.map((o) => (
            <button key={o.id} className={styles.syncOption} onClick={o.action}>
              <span className={styles.syncIcon}>{o.icon}</span>
              <div>
                <p className={styles.syncLabel}>{o.label}</p>
                <p className={styles.syncDesc}>{o.desc}</p>
              </div>
              <svg className={styles.chevron} width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 18l6-6-6-6" stroke="#aaa" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Upload Dialog — sample data ───────────────────────────────────────────────
const SAMPLE_CSV = `name,phone,email,category,notes
Rahul Sharma,"+919876543210",rahul@example.com,customer,VIP client
Priya Singh,"+12025551234",priya@example.com,friends,College friend
Amit Verma,"+917508391792",,colleagues,Team lead`;

const SAMPLE_VCF = `BEGIN:VCARD
VERSION:3.0
FN:Rahul Sharma
TEL;TYPE=CELL:+919876543210
EMAIL:rahul@example.com
END:VCARD
BEGIN:VCARD
VERSION:3.0
FN:Priya Singh
TEL;TYPE=CELL:+12025551234
EMAIL:priya@example.com
END:VCARD`;

// ── Parsers ───────────────────────────────────────────────────────────────────
function fixExcelPhone(val) {
  if (!val) return val;
  const trimmed = val.trim();
  if (/^[\d.]+[eE][+\-]\d+$/.test(trimmed)) {
    try {
      const num = parseFloat(trimmed);
      if (!isNaN(num)) {
        const full = num.toLocaleString("fullwide", { maximumFractionDigits: 0 }).replace(/,/g, "");
        const digits = full.replace(/\D/g, "");
        if (digits.length >= 10) return "+" + digits;
        return full;
      }
    } catch { }
  }
  return trimmed;
}

function parseCSVText(text) {

  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/"/g, ""));
  return lines
    .slice(1)
    .map((line, idx) => {
      const vals = line.match(/(".*?"|[^,]+|(?<=,)(?=,)|^(?=,)|(?<=,)$)/g) || line.split(",");
      const clean = vals.map((v) => v?.replace(/^"|"$/g, "").trim() || "");
      const obj = { _id: `csv_${idx}` };
      headers.forEach((h, i) => {
        obj[h] = clean[i] || "";
      });
      // Normalize common header names
      if (!obj.name && obj.fullname) obj.name = obj.fullname;
      if (!obj.name && obj["full name"]) obj.name = obj["full name"];
      if (!obj.phone && obj["phone number"]) obj.phone = obj["phone number"];
      if (!obj.phone && obj.mobile) obj.phone = obj.mobile;
      if (!obj.email && obj["email address"]) obj.email = obj["email address"];
      // Fix Excel scientific notation for phone fields
      if (obj.phone) obj.phone = fixExcelPhone(obj.phone);
      if (obj.mobile) obj.mobile = fixExcelPhone(obj.mobile);
      if (obj["phone number"]) obj["phone number"] = fixExcelPhone(obj["phone number"]);
      return obj;
    })
    .filter((r) => Object.values(r).some((v) => v && v !== r._id));
}

function parseVCFText(text) {
  const cards = text.split(/BEGIN:VCARD/i).filter((c) => c.trim());
  return cards
    .map((card, idx) => {
      const lines = card
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      const get = (prefix) => {
        const line = lines.find((l) => l.toUpperCase().startsWith(prefix.toUpperCase()));
        return line ? line.split(":").slice(1).join(":").trim() : "";
      };
      const telLine = lines.find((l) => /^TEL/i.test(l));
      const phone = telLine ? telLine.split(":").slice(1).join(":").trim() : "";
      const emailLine = lines.find((l) => /^EMAIL/i.test(l));
      const email = emailLine ? emailLine.split(":").slice(1).join(":").trim() : "";
      const name = get("FN") || get("N").split(";").filter(Boolean).reverse().join(" ").trim();
      const catLine = lines.find(l => /^CATEGORIES/i.test(l));
      const rawCat = catLine ? catLine.split(":").slice(1).join(":").trim().toLowerCase() : "";
      const KNOWN_CATS = ["family", "friends", "colleagues", "customer", "other"];
      const category = KNOWN_CATS.find(c => rawCat.includes(c)) || (rawCat ? rawCat.split(/[,;]/)[0].trim().replace(/\s+/g, "_") : "other");
      return { _id: `vcf_${idx}`, name, phone, email, category, notes: "" };
    })
    .filter((r) => r.name || r.phone);
}

// ── Validators ────────────────────────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getIssues(row, allRows) {
  const issues = [];
  if (!row.name?.trim()) issues.push({ field: "name", msg: "Name is missing" });

  const phoneVal = validatePhone(row.phone);
  if (!row.phone?.trim()) issues.push({ field: "phone", msg: "Phone is missing" });
  else if (!phoneVal.valid) issues.push({ field: "phone", msg: "Invalid phone format" });

  if (row.email?.trim() && !EMAIL_RE.test(row.email.trim()))
    issues.push({ field: "email", msg: "Invalid email format" });

  // Duplicate phone check across all rows (skip self)
  if (row.phone?.trim()) {
    const dupe = allRows.find((r) => r._id !== row._id && r.phone?.trim() === row.phone?.trim());
    if (dupe) issues.push({ field: "phone", msg: "Duplicate phone number" });
  }
  return issues;
}

// ── Preview Card ──────────────────────────────────────────────────────────────
const PreviewCard = ({ row, allRows, onRemove, onEdit }) => {
  const issues = getIssues(row, allRows);
  const hasIssue = issues.length > 0;
  const phoneVal = validatePhone(row.phone);

  return (
    <div style={{
      border: `1.5px solid ${hasIssue ? "#FBBF24" : "#eee"}`,
      borderRadius: 10,
      padding: "0px 12px",
      margin: "0 0 4px",
      background: hasIssue ? "#FFFBEB" : "#fff",
      display: "flex",
      alignItems: "center",
      gap: 8,
      height: CARD_HEIGHT - 16,
      boxSizing: "border-box",
    }}>
      <Avatar name={row.name || "?"} size={32} />
      <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: row.name?.trim() ? "#222" : "#C05621", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {row.name?.trim() || <span style={{ fontStyle: "italic" }}>No name</span>}
        </div>
        <div style={{ fontSize: 11, color: phoneVal.valid ? "#555" : "#C05621" }}>
          {row.phone?.trim() ? (phoneVal.valid ? phoneVal.display : `${row.phone}`) : <span style={{ fontStyle: "italic", color: "#C05621" }}>No phone</span>}
        </div>
        <div style={{ marginTop: 3 }}>
          <CategoryBadge cat={row.category || "other"} />
        </div>
        {hasIssue && (
          <div style={{ display: "flex", gap: 3, marginTop: 2 }}>
            {issues.slice(0, 2).map((iss, i) => (
              <span key={i} style={{ fontSize: 9, background: "#FEF3C7", color: "#92400E", borderRadius: 3, padding: "1px 4px", fontWeight: 600 }}>{iss.msg}</span>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        <button onClick={() => onEdit?.(row)} style={{ background: "#eef2ff00", border: "none", cursor: "pointer" }}>
          <img src="/svg/edit-icon3.svg" alt="edit-icon3" />
        </button>
        <button onClick={() => onRemove(row._id)} style={{ background: "#fff0f000", border: "none", cursor: "pointer" }}>
          <img src="/svg/delete-icon3.svg" alt="delete-icon3" />
        </button>
      </div>
    </div>
  );
};

// ── Upload Dialog ─────────────────────────────────────────────────────────────
const CARD_HEIGHT = 90;

// Virtualised row pitch for the My Contacts list. Must stay >= the tallest
// .contactCard (89px measured) + the 16px the row renderer insets, or the card
// overflows react-window's scroll container and produces a stray scrollbar.
const CONTACT_ROW_HEIGHT = 112;

const PreviewEditModal = ({ row, onSave, onClose }) => {
  const [draft, setDraft] = useState({ ...row });
  const setField = (k, v) => setDraft(prev => ({ ...prev, [k]: k === "name" ? String(v).replace(/[^a-zA-Z\s.\-']/g, "") : k === "phone" ? v.replace(/[^\d+]/g, "") : v }));
  const fieldStyle = { width: "100%", border: "1px solid #E0E5F2", borderRadius: 12, fontSize: 13, boxSizing: "border-box", marginTop: 5 };
  return (
    <NewModal isOpen={true} onClose={onClose} title="Edit Contact" height="auto" maxHeight="80vh">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <label style={{ fontSize: 16, color: "#24252c", fontWeight: 600 }}>NAME</label>
          <input className={styles.inputEdit} value={draft.name || ""} maxLength={40} onChange={(e) => setField("name", e.target.value)} placeholder="Full name" style={fieldStyle} />
        </div>

        <div className={styles.phoneField}>
          {/* <label style={{ fontSize: 16, color: "#24252c", fontWeight: 600 }}>PHONE</label> */}
          {/* <input className={styles.inputEdit} value={draft.phone || ""} maxLength={16} onFocus={() => phoneOnFocus(draft.phone, (v) => setField("phone", v))} onChange={(e) => phoneOnChange(e.target.value, (v) => setField("phone", v))} placeholder="+1 2025551234" style={fieldStyle} />
          {draft.phone && draft.phone !== "+" && (() => {
            const c = validatePhone(draft.phone);
            return c.valid ? <p style={{ fontSize: 11, color: "#2D7A4F", margin: "3px 0 0" }}>{c.display} ({c.country})</p> : <p style={{ fontSize: 11, color: "#C05621", margin: "3px 0 0" }}>Use international format e.g. +1 2025551234</p>;
          })()} */}
          <label style={{ fontSize: 16, color: "#24252c", fontWeight: 600 }}>PHONE</label>
          <PhoneInput
            country={DEFAULT_PHONE_COUNTRY}
            value={draft.phone?.startsWith("+") ? draft.phone.slice(1) : draft.phone || ""}
            onChange={(val) => setField("phone", "+" + val)}
            inputStyle={{ ...fieldStyle, marginTop: 5, width: "100%" }}
            containerStyle={{ width: "100%", marginTop: 5 }}
            enableSearch
          />
          {draft.phone && draft.phone !== "+" && draft.phone !== "+1" && (() => {
            const c = validatePhone(draft.phone);
            return c.valid
              ? <p style={{ fontSize: 11, color: "#2D7A4F", margin: "3px 0 0" }}>{c.display} ({c.country})</p>
              : <p style={{ fontSize: 11, color: "#C05621", margin: "3px 0 0" }}>Use international format e.g. +1 2025551234</p>;
          })()}
        </div>
        <div>
          <label style={{ fontSize: 16, color: "#24252c", fontWeight: 600 }}>EMAIL</label>
          <input className={styles.inputEdit} value={draft.email || ""} maxLength={50} onChange={(e) => setField("email", e.target.value)} placeholder="email@example.com" style={fieldStyle} />
        </div>
        <div>
          <label style={{ fontSize: 16, color: "#24252c", fontWeight: 600 }}>CATEGORY</label>
          <select className={styles.formInput2} value={draft.category || "other"} onChange={(e) => setField("category", e.target.value)} style={fieldStyle}>
            {["family", "friends", "colleagues", "customer", "other"].map((c) => (
              <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </select>
        </div>
        {/* <button onClick={() => { onSave(draft); onClose(); }} style={{ padding: "10px", background: "#6524eb", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", marginTop: 8 }}>
          Save
        </button> */}
        <AnimatedButton
          label={"Save"}
          positionType="relative"
          width="100%"
          bottom="0px"
          onClick={() => { onSave(draft); onClose(); }}
        />
      </div>
    </NewModal>
  );
};

// Exported — the dialer's Contacts tab reuses this same CSV/VCF import sheet.
export const UploadDialog = ({ isOpen, onClose, onSync, onImportDone }) => {
  const [step, setStep] = useState("pick");
  const [editingRow, setEditingRow] = useState(null);
  const [listHeight, setListHeight] = useState(300);
  const listContainerRef = useRef();
  const [dragging, setDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [fileExt, setFileExt] = useState("csv");
  const [uploadErr, setUploadErr] = useState("");
  const fileRef = useRef();
  const [previewFilter, setPreviewFilter] = useState("total"); // "total" | "valid" | "invalid"
  // Reset step when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep("pick");
      setRows([]);
      setUploadErr("");
      setPreviewFilter("total");
    }
  }, [isOpen]);

  const downloadSample = (type) => {
    const content = type === "csv" ? SAMPLE_CSV : SAMPLE_VCF;
    const mime = type === "csv" ? "text/csv" : "text/vcard";
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contacts_sample.${type}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["csv", "vcf"].includes(ext)) {
      setUploadErr("Only .csv and .vcf files are supported.");
      return;
    }
    setUploadErr("");
    setFileName(file.name);
    setFileExt(ext);
    setParsing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = ext === "vcf" ? parseVCFText(text) : parseCSVText(text);
      setRows(parsed);
      setParsing(false);
      setStep("preview");
      setTimeout(() => {
        if (listContainerRef.current) setListHeight(listContainerRef.current.clientHeight || 300);
      }, 100);
    };
    reader.readAsText(file);
  };

  const updateRow = (updated) => setRows((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r._id !== id));

  const validRows = rows.filter((r) => getIssues(r, rows).length === 0);
  const invalidRows = rows.filter((r) => getIssues(r, rows).length > 0);
  const displayRows = previewFilter === "valid"
    ? rows.filter(r => getIssues(r, rows).length === 0)
    : previewFilter === "invalid"
      ? rows.filter(r => getIssues(r, rows).length > 0)
      : rows;
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const rowsToFile = () => {
    const latestRows = rowsRef.current;
    const currentValidRows = latestRows.filter((r) => getIssues(r, latestRows).length === 0);
    const source = fileExt === "vcf" ? "vcf_upload" : "csv_upload";
    const header = "name,phone,email,category,notes,source";
    const lines = currentValidRows.map((r) => {
      const phoneVal = validatePhone(r.phone);
      const phone = phoneVal.valid ? phoneVal.e164 : r.phone || "";
      return [r.name || "", phone, r.email || "", r.category || "other", r.notes || "", source]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",");
    });
    return new File([[header, ...lines].join("\n")], "contacts_upload.csv", { type: "text/csv" });
  };

  const handleUpload = async () => {
    if (validRows.length === 0) return;
    setUploading(true);
    setUploadErr("");
    try {
      const res = await onSync?.("file", rowsToFile());
      const callback = onImportDone;
      onClose();
      callback?.(res);
    } catch (err) {
      console.error("[UploadDialog] upload error:", err);
      setUploadErr("Upload failed. Please try again.");
      setUploading(false);
    }
  };

  return (
    <>
      <NewModal
        isOpen={isOpen}
        onClose={onClose}
        title={step === "pick" ? "Upload Contacts File" : `Preview (${rows.length})`}
        height={step === "preview" ? "auto" : "auto"}
        maxHeight="92vh"
      >
        {step === "pick" ? (
          <>
            <p style={{ fontSize: 12, color: "#AAAAAA", marginBottom: 14 }}>
              Import contacts from a .csv or .vcf file
            </p>

            {/* Drag & Drop */}
            <div
              className={styles.uploadArea}
              style={dragging ? { borderColor: "#6524eb", background: "#f5f0ff" } : {}}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                handleFile(e.dataTransfer.files[0]);
              }}
            >
              {parsing ? (
                <>
                  <div className={styles.spinner} style={{ width: 36, height: 36, borderWidth: 3 }} />
                  <p className={styles.uploadTitle}>Parsing file...</p>
                </>
              ) : (
                <>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                      stroke="#6524eb"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <polyline points="17 8 12 3 7 8" stroke="#6524eb" strokeWidth="2" strokeLinecap="round" />
                    <line x1="12" y1="3" x2="12" y2="15" stroke="#6524eb" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <p className={styles.uploadTitle}>Drag & drop or click to upload</p>
                  <p className={styles.uploadSub}>Supports .csv and .vcf files</p>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.vcf"
                hidden
                onChange={(e) => handleFile(e.target.files[0])}
              />
            </div>

            {uploadErr && (
              <p style={{ fontSize: 13, color: "#D63B3B", marginTop: 8, textAlign: "center" }}>{uploadErr}</p>
            )}

            {/* Sample downloads */}
            <div
              style={{
                marginTop: 20,

              }}
            >
              <p style={{ fontSize: 12, color: "#AAAAAA", fontWeight: 500, marginBottom: 20, textAlign: "center" }}>
                Not sure about the format? Download a sample:
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                {["csv", "vcf"].map((t) => (
                  <button
                    key={t}
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadSample(t);
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      background: "#6524eb",
                      border: "1.5px solid #6524eb",
                      borderRadius: 8,
                      fontSize: 12,
                      color: "#ffffff",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                    }}
                  >

                    Sample {t.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          /* ── PREVIEW STEP ── */
          <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {/* Back button */}
            <button
              onClick={() => {
                setStep("pick");
                setRows([]);
              }}
              style={{
                alignSelf: "flex-start",
                marginBottom: 12,
                fontSize: 13,
                color: "#6524eb",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                fontWeight: 500,
                background: "#EFE9FF",
                padding: "4px 12px",
                borderRadius: "10px",
              }}
            >
              ← Back
            </button>

            {/* Stats bar */}
            {/* <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <div
              style={{ flex: 1, textAlign: "center", padding: "8px 0", background: "#F0FFF4", borderRadius: 8 }}
            >
              <div style={{ fontSize: 18, fontWeight: 700, color: "#2D7A4F" }}>{validRows.length}</div>
              <div style={{ fontSize: 11, color: "#2D7A4F", fontWeight: 500 }}>Ready</div>
            </div>
            <div
              style={{
                flex: 1,
                textAlign: "center",
                padding: "8px 0",
                background: invalidRows.length > 0 ? "#FFFBEB" : "#f5f5f5",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: invalidRows.length > 0 ? "#92400E" : "#aaa",
                }}
              >
                {invalidRows.length}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: invalidRows.length > 0 ? "#92400E" : "#aaa",
                  fontWeight: 500,
                }}
              >
                Need Fix
              </div>
            </div>
            <div
              style={{ flex: 1, textAlign: "center", padding: "8px 0", background: "#EEF2FF", borderRadius: 8 }}
            >
              <div style={{ fontSize: 18, fontWeight: 700, color: "#4338CA" }}>{rows.length}</div>
              <div style={{ fontSize: 11, color: "#4338CA", fontWeight: 500 }}>Total</div>
            </div>
          </div> */}
            {/* Stats bar */}
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <div
                onClick={() => setPreviewFilter("valid")}
                style={{ flex: 1, textAlign: "center", padding: "8px 0", background: previewFilter === "valid" ? "#FFEBA5" : "#FFEBA5", borderRadius: 8, cursor: "pointer", border: previewFilter === "valid" ? "2px solid #FFE074" : "2px solid transparent" }}
              >
                <div style={{ fontSize: 40, fontWeight: 700, color: "#24252C" }}>{validRows.length}</div>
                <div style={{ fontSize: 14, color: "#24252c", fontWeight: 500 }}>Ready</div>
              </div>
              <div
                onClick={() => setPreviewFilter("invalid")}
                style={{ flex: 1, textAlign: "center", padding: "8px 0", background: previewFilter === "invalid" ? (invalidRows.length > 0 ? "#BFF7F7" : "#BFF7F7") : (invalidRows.length > 0 ? "#BFF7F7" : "#BFF7F7"), borderRadius: 8, cursor: invalidRows.length > 0 ? "pointer" : "default", border: previewFilter === "invalid" ? "2px solid #9AF3EF" : "2px solid transparent" }}
              >
                <div style={{ fontSize: 40, fontWeight: 700, color: invalidRows.length > 0 ? "#24252c" : "#24252c" }}>
                  {invalidRows.length}
                </div>
                <div style={{ fontSize: 14, color: invalidRows.length > 0 ? "#24252c" : "#24252c", fontWeight: 500 }}>Need Fix</div>
              </div>
              <div
                onClick={() => setPreviewFilter("total")}
                style={{ flex: 1, textAlign: "center", padding: "8px 0", background: previewFilter === "total" ? "#E2D2FF" : "#E2D2FF", borderRadius: 8, cursor: "pointer", border: previewFilter === "total" ? "2px solid #CFBAFF" : "2px solid transparent" }}
              >
                <div style={{ fontSize: 40, fontWeight: 700, color: "#24252c" }}>{rows.length}</div>
                <div style={{ fontSize: 14, color: "#24252c", fontWeight: 500 }}>Total</div>
              </div>
            </div>
            {/* File name hint */}
            <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>
              📄 {fileName}
              {invalidRows.length > 0 && (
                <span style={{ marginLeft: 8, color: "#92400E", fontWeight: 500 }}>
                  — Fix issues before importing, or they'll be skipped
                </span>
              )}
            </div>

            {/* Cards — Virtualized with react-window */}
            <div ref={listContainerRef} style={{ flex: 1 }}>
              {rows.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa" }}>
                  <p>No contacts found in this file.</p>
                  <button
                    onClick={() => setStep("pick")}
                    style={{
                      marginTop: 12,
                      padding: "8px 20px",
                      background: "#6524eb",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontSize: 13,
                    }}
                  >
                    Try another file
                  </button>
                </div>
              ) : (
                // <FixedSizeList
                //   height={listHeight}
                //   itemCount={rows.length}
                //   itemSize={CARD_HEIGHT}
                //   width="100%"
                //   overscanCount={5}
                // >
                //   {({ index, style }) => (
                //     <div style={style}>
                //       <PreviewCard
                //         key={rows[index]._id}
                //         row={rows[index]}
                //         allRows={rows}
                //         onRemove={removeRow}
                //         onEdit={(row) => setEditingRow(row)}
                //       />
                //     </div>
                //   )}
                // </FixedSizeList>
                <FixedSizeList
                  height={listHeight}
                  itemCount={displayRows.length}   // ← rows → displayRows
                  itemSize={CARD_HEIGHT}
                  width="100%"
                  overscanCount={5}
                >
                  {({ index, style }) => (
                    <div style={style}>
                      <PreviewCard
                        key={displayRows[index]._id}
                        row={displayRows[index]}
                        allRows={rows}
                        onRemove={removeRow}
                        onEdit={(row) => setEditingRow(row)}
                      />
                    </div>
                  )}
                </FixedSizeList>
              )}
            </div>

            {/* Footer */}
            {uploadErr && (
              <p style={{ fontSize: 13, color: "#D63B3B", textAlign: "center", margin: "8px 0" }}>{uploadErr}</p>
            )}
            <div style={{ display: "flex", gap: 10, paddingTop: 12 }}>
              {/* <button
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: "11px 0",
                  background: "#f5f5f5",
                  border: "none",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button> */}
              {/* <button
                disabled={validRows.length === 0 || uploading}
                onClick={handleUpload}
                style={{
                  flex: 2,
                  padding: "11px 0",
                  border: "none",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  background: "#6524eb",
                  color: "#fff",
                  opacity: validRows.length === 0 || uploading ? 0.45 : 1,
                  cursor: validRows.length === 0 || uploading ? "not-allowed" : "pointer",
                }}
              >
                {uploading
                  ? "Importing..."
                  : `Import ${validRows.length} Contact${validRows.length !== 1 ? "s" : ""}`}
              </button> */}
              <AnimatedButton
                label={uploading
                  ? "Importing..."
                  : `Import ${validRows.length} Contact${validRows.length !== 1 ? "s" : ""}`}
                positionType="relative"
                width="100%"
                bottom="0px"
                disabled={validRows.length === 0 || uploading}
                onClick={handleUpload}
              />
            </div>
          </div>
        )}
      </NewModal>

      {/* Edit modal — opens on top of UploadDialog */}
      {editingRow && (
        <PreviewEditModal
          row={editingRow}
          onSave={(updated) => {
            updateRow(updated);
            setEditingRow(null);
          }}
          onClose={() => setEditingRow(null)}
        />
      )}
    </>
  );
};

// ── Contact Add/Edit Modal ────────────────────────────────────────────────────
// Exported — the dialer's Contacts tab reuses this same add/edit contact sheet.
export const ContactModal = ({ isOpen, contact, onClose, onSave, categories }) => {
  const cats = categories || ["family", "friends", "colleagues", "customer", "other"];
  const [saveError, setSaveError] = useState("");
  const [form, setForm] = useState({
    name: contact?.name || "",
    phone: contact?.phone || "",
    email: contact?.email || "",
    category: contact?.category || cats[0] || "other",
    transferTo: contact?.transferTo || "",
    transferType: contact?.transferType || "cold",
    notes: contact?.notes || "",
    isVip: contact?.isVip || false,
  });

  useEffect(() => {
    setForm({
      name: contact?.name || "",
      phone: contact?.phone || "",
      email: contact?.email || "",
      category: contact?.category || cats[0] || "other",
      transferTo: contact?.transferTo || "",
      transferType: contact?.transferType || "cold",
      notes: contact?.notes || "",
      isVip: contact?.isVip || false,
    });
  }, [contact, isOpen]);

  const maxLengths = { phone: 16, name: 40, email: 50, notes: 200 };
  const sanitizeName = (v) => String(v).replace(/[^a-zA-Z\s.\-']/g, "");
  const setField = (key, val) => {
    let cleaned = key === "name" ? sanitizeName(val) : key === "phone" ? val.replace(/[^\d+]/g, "") : val;
    const max = maxLengths[key];
    const trimmed = max ? String(cleaned).slice(0, max) : cleaned;
    setForm((prev) => ({ ...prev, [key]: trimmed }));
  };

  return (
    <NewModal
      isOpen={isOpen}
      onClose={onClose}
      title={contact ? "Edit Contact" : "Add Contact"}
      height="auto"
      maxHeight="92vh"
    >
      <div className={styles.formGrid}>
        <div className={styles.formField}>
          <label>Name *</label>
          <input
            value={form.name}
            maxLength={40}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="Full name"
            className={styles.formInput}
          />
        </div>

        {/* ── Phone field with + auto-prefix ── */}
        {/* phoneField constrains react-phone-input-2's 300px country dropdown
            to this grid column — see .phoneField in Contactmanager.module.css. */}
        <div className={`${styles.formField} ${styles.phoneField}`}>
          {/* <label>Phone *</label>
          <input
            value={form.phone}
            maxLength={16}
            onFocus={() => phoneOnFocus(form.phone, (val) => setField("phone", val))}
            onChange={(e) => phoneOnChange(e.target.value, (val) => setField("phone", val))}
            placeholder="+1 (555) 000-0000"
            className={`${styles.formInput} ${form.phone && form.phone !== "+" && !validatePhone(form.phone).valid ? styles.inputWarn : ""
              }`}
          />
          {form.phone &&
            form.phone !== "+" &&
            (() => {
              const check = validatePhone(form.phone);
              if (!check.valid)
                return (
                  <p className={styles.fieldWarnText}>
                    ⚠ Looks invalid — use international format e.g. +1 555 000 0000
                  </p>
                );
              return (
                <p className={styles.fieldOkText}>
                  ✓ {check.display} ({check.country})
                </p>
              );
            })()} */}
          <label>Phone *</label>
          <PhoneInput
            country={DEFAULT_PHONE_COUNTRY}
            value={form.phone.startsWith("+") ? form.phone.slice(1) : form.phone}
            onChange={(val) => setField("phone", "+" + val)}
            inputClass={`${styles.formInput} ${form.phone && form.phone !== "+" && !validatePhone(form.phone).valid ? styles.inputWarn : ""}`}
            containerStyle={{ width: "100%" }}
            inputStyle={{ width: "100%" }}
            enableSearch
          />
          {form.phone && form.phone !== "+" && form.phone !== "+1" && (() => {
            const check = validatePhone(form.phone);
            if (!check.valid)
              return <p className={styles.fieldWarnText}>⚠ Looks invalid — use international format e.g. +1 555 000 0000</p>;
            return <p className={styles.fieldOkText}>✓ {check.display} ({check.country})</p>;
          })()}
        </div>

        <div className={styles.formField}>
          <label>Email</label>
          <input
            value={form.email}
            maxLength={50}
            onChange={(e) => setField("email", e.target.value)}
            placeholder="email@example.com"
            className={`${styles.formInput} ${form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ? styles.inputWarn : ""}`}
          />
          {form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && (
            <p className={styles.fieldWarnText}>⚠ Looks invalid — please enter a valid email address</p>
          )}
        </div>

        <div className={styles.formField}>
          <label>Category</label>
          <select
            value={form.category}
            onChange={(e) => setField("category", e.target.value)}
            className={styles.formInput2}
          >
            {cats.map((c) => (
              <option key={c} value={c}>
                {c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
              </option>
            ))}
          </select>
        </div>
        {/* 
        <div className={styles.formField} style={{ gridColumn: "1/-1" }}>
          <label>
            Transfer To (optional)
            <InfoTooltip text="Override the default forward number just for this contact." />
          </label>
          <input
            value={form.transferTo}
            onChange={(e) => setField("transferTo", e.target.value)}
            placeholder="Override forward number e.g. +19876543210"
            className={styles.formInput}
          />
        </div>

        <div className={styles.formField}>
          <label>Transfer Type</label>
          <select
            value={form.transferType}
            onChange={(e) => setField("transferType", e.target.value)}
            className={styles.formInput}
          >
            <option value="silent">Silent (instant, no speech)</option>
            <option value="cold">Cold (brief "please hold")</option>
            <option value="warm">Warm (AI announces caller to receiver)</option>
          </select>
        </div>
        */}
        {/* <div className={styles.formField} style={{ gridColumn: "1/-1" }}>
          <label>Notes</label>
          <textarea
            value={form.notes}
            maxLength={200}
            onChange={(e) => setField("notes", e.target.value)}
            placeholder="Optional notes..."
            className={styles.formInput}
            rows={2}
          />
        </div> */}

        {contact?.source !== "auto_call" && (
          <div className={styles.formField} style={{ gridColumn: "1/-1" }}>
            <label className={styles.vipToggleRow}>
              <span>
                VIP
                <InfoTooltip text="VIP contacts get instant call transfer with no AI qualification. Calls are forwarded directly to your phone." />
              </span>
              <div
                className={`${styles.toggle} ${form.isVip ? styles.toggleOn : ""}`}
                onClick={() => setField("isVip", !form.isVip)}
              >
                <div className={styles.toggleThumb} />
              </div>
            </label>
            {form.isVip && (
              <p className={styles.fieldOkText}>
                Calls from this contact will be transferred instantly — no AI screening.
              </p>
            )}
          </div>
        )}
      </div>

      {saveError && (
        <div style={{ padding: "8px 12px", background: "#FFEBEE", borderRadius: 8, marginTop: 8, fontSize: 13, color: "#C62828", fontWeight: 500 }}>
          {saveError}
        </div>
      )}
      {/* <div style={{ display: "flex", gap: 10, marginTop: 12, paddingBottom: 4 }}>
        <button
          disabled={!form.name.trim() || !form.phone.trim()}
          onClick={async () => {
            setSaveError("");
            const result = await onSave(form);
            if (result?.error) {
              setSaveError(result.error);
              return;
            }
            onClose();
          }}
          style={{
            flex: 1,
            padding: "11px 0",
            border: "none",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            background: "#6524eb",
            color: "#fff",
            opacity: !form.name.trim() || !form.phone.trim() ? 0.45 : 1,
            cursor: !form.name.trim() || !form.phone.trim() ? "not-allowed" : "pointer",
          }}
        >
          {contact ? "Save Changes" : "Add Contact"}
        </button>
      </div> */}
      <div style={{ marginTop: 12, paddingBottom: 4 }}>
        <AnimatedButton
          label={contact ? "Save Changes" : "Add Contact"}
          positionType="relative"
          width="100%"
          bottom="0px"
          disabled={!form.name.trim() || !form.phone.trim() || (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))}
          onClick={async () => {
            setSaveError("");
            const result = await onSave(form);
            if (result?.error) {
              setSaveError(result.error);
              return;
            }
            onClose();
          }}
        />
      </div>
    </NewModal>
  );
};

// ── Add Category Modal ────────────────────────────────────────────────────────
const AddCategoryModal = ({ isOpen, onClose, activeCats, agentId, onAdd }) => {
  const [newCategoryName, setNewCategoryName] = useState("");

  useEffect(() => {
    if (!isOpen) setNewCategoryName("");
  }, [isOpen]);

  const handleAdd = () => {
    const key = newCategoryName.trim().toLowerCase().replace(/\s+/g, "_");
    if (!activeCats.includes(key)) onAdd(key);
    onClose();
  };

  return (
    <NewModal isOpen={isOpen} onClose={onClose} title="Add Category" height="auto" maxHeight="50vh">
      <div className={styles.formField} style={{ marginBottom: 16 }}>
        <label>Category Name</label>
        <input
          value={newCategoryName}
          onChange={(e) => setNewCategoryName(e.target.value)}
          placeholder="e.g. Warranty, Vendor, Partner"
          className={styles.formInput}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && newCategoryName.trim()) handleAdd();
          }}
        />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button
          onClick={onClose}
          style={{
            flex: 1,
            padding: "11px 0",
            background: "#f5f5f5",
            border: "none",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          disabled={!newCategoryName.trim()}
          onClick={handleAdd}
          style={{
            flex: 1,
            padding: "11px 0",
            border: "none",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            background: "#6524eb",
            color: "#fff",
            opacity: !newCategoryName.trim() ? 0.45 : 1,
            cursor: !newCategoryName.trim() ? "not-allowed" : "pointer",
          }}
        >
          Add
        </button>
      </div>
    </NewModal>
  );
};

// ── Contact Card ──────────────────────────────────────────────────────────────
// Viewport coords for a card's ⋯ menu. The rows live inside react-window's
// FixedSizeList, whose OUTER div carries inline `overflow:auto` AND
// `will-change:transform`. That will-change makes it a containing block, so a
// `position:fixed` menu still resolves against the list and is clipped by its
// overflow — fixed alone cannot escape it. The menu is therefore rendered
// through createPortal to document.body (same approach as
// BulkMoveCategoryMenu below) and placed with these coords, which is what
// actually gets it out of the virtualised list. Because it is then no longer a
// DOM child of the card, outside-click has to test the portalled node too —
// see popRef in the card components.
const useAnchoredMenuStyle = (anchorRef, open) => {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const MENU_W = 180; // .dropMenu min-width
      const GAP = 4; // matches .dropMenu's old top: calc(100% + 4px)
      const spaceBelow = window.innerHeight - r.bottom;
      // Open upward when the menu would run off the bottom of the viewport.
      const flipUp = spaceBelow < 220 && r.top > spaceBelow;
      setPos({
        position: "fixed",
        // Right-align to the button (matching .dropMenu's right:0), clamped so
        // the menu can never sit off either edge of the screen.
        left: Math.round(
          Math.max(8, Math.min(r.right - MENU_W, window.innerWidth - MENU_W - 8)),
        ),
        right: "auto",
        ...(flipUp
          ? { bottom: Math.round(window.innerHeight - r.top + GAP), top: "auto" }
          : { top: Math.round(r.bottom + GAP), bottom: "auto" }),
      });
    };
    update();
    // capture:true so .listArea's own scroll is caught, not just the window's.
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchorRef]);
  return pos;
};

const ContactCard = React.memo(({
  contact,
  onEdit,
  onBlock,
  onUnblock,
  onDelete,
  bulkMode,
  isSelected,
  onSelectToggle,
  readOnly = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef();
  // The portalled menu node — needed so outside-click can still tell "inside
  // the menu" from "outside" once the menu leaves the card's subtree.
  const popRef = useRef();
  const menuPos = useAnchoredMenuStyle(menuRef, menuOpen);
  const phoneNeedsCheck = contact.phone && !contact.phone.startsWith("+");
  const phoneCheck = phoneNeedsCheck
    ? validatePhone(contact.phone)
    : { valid: true, e164: contact.phone, display: contact.phone, country: null };

  useEffect(() => {
    const h = (e) => {
      const inAnchor = menuRef.current && menuRef.current.contains(e.target);
      const inMenu = popRef.current && popRef.current.contains(e.target);
      if (!inAnchor && !inMenu) setMenuOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div
      className={`${styles.contactCard} ${!phoneCheck.valid ? styles.contactCardWarn : ""} ${contact.isBlacklisted ? styles.contactCardBlocked : ""
        }`}
      style={menuOpen ? { position: "relative", zIndex: 100 } : {}}
    >
      <div className={styles.bulkCheckboxMain}>
        {bulkMode && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onSelectToggle}
            className={styles.bulkCheckbox}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <Avatar name={contact.name} />
      </div>
      <div className={styles.contactInfo}>
        <div className={styles.contactName}>{contact.name || "—"}</div>
        <div className={styles.contactPhone}>
          {contact.phone}
          {phoneCheck.valid && phoneCheck.country && (
            <span className={styles.countryFlag} title={phoneCheck.country}>
              {" "}
              {phoneCheck.country}
            </span>
          )}
        </div>
        {!phoneCheck.valid && (
          <div className={styles.phoneWarning}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path
                d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                stroke="#C05621"
                strokeWidth="2"
                fill="none"
              />
              <line x1="12" y1="9" x2="12" y2="13" stroke="#C05621" strokeWidth="2" strokeLinecap="round" />
              <line x1="12" y1="17" x2="12.01" y2="17" stroke="#C05621" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span>Looks invalid — no E.164 match</span>
            {!readOnly && (
              <button className={styles.fixBtn} onClick={() => onEdit(contact)}>
                Fix
              </button>
            )}
          </div>
        )}
        <div className={styles.contactMeta}>
          {contact.source === "auto_call" && (
            <>
              <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, fontWeight: 600, background: "#F3E5F5", color: "#7B1FA2" }}>Auto</span>
              {contact.notes?.includes("Auto-saved via ") ? (
                <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, fontWeight: 500, background: "#E3F2FD", color: "#1565C0" }}>
                  {contact.notes.split("Auto-saved via ")[1]?.split(" |")[0] || ""}
                </span>
              ) : null}
            </>
          )}
          {contact.isVip && <span className={styles.vipBadge}>&#9733; VIP</span>}
          <CategoryBadge cat={contact.category} />
          {contact.isBlacklisted && <span className={styles.blockedBadge}>Blocked</span>}
          {contact.lastCallAt && (
            <span className={styles.lastCall}>Last call: {formatLocalDT(contact.lastCallAt)}</span>
          )}
          {contact.callCount > 0 && (
            <span className={styles.lastCall}>
              {contact.callCount} call{contact.callCount !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        {/* Last message preview for auto contacts */}
        {contact.source === "auto_call" && contact.notes && contact.notes.includes("Reason:") && (
          <div style={{ fontSize: 11, color: "#555", marginTop: 4, padding: "4px 8px", background: "#F5F5F5", borderRadius: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}
            title={contact.notes.split("Reason: ")[1]?.split(" |")[0] || ""}
          >
            💬 {(contact.notes.split("Reason: ")[1]?.split(" |")[0] || "").substring(0, 60)}{(contact.notes.split("Reason: ")[1]?.split(" |")[0] || "").length > 60 ? "..." : ""}
          </div>
        )}
      </div>
      {/* Delete aur action menu — support ke liye dono band. */}
      <div
        onClick={() => onDelete?.(contact)}
        style={readOnly ? { display: "none" } : { cursor: "pointer" }}
      >
        <img src="/svg/delete-icon3.svg" alt="delete-icon3" />
      </div>
      <div
        className={styles.cardActions}
        ref={menuRef}
        style={readOnly ? { display: "none" } : undefined}
      >
        <button className={styles.menuBtn} onClick={() => setMenuOpen((p) => !p)}>
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.5" y="0.5" width="29" height="29" rx="9.5" fill="white" stroke="#E8E9EE" />
            <circle cx="8" cy="15" r="2" fill="#24252C" />
            <circle cx="15" cy="15" r="2" fill="#24252C" />
            <circle cx="22" cy="15" r="2" fill="#24252C" />
          </svg>
        </button>
        {menuOpen && menuPos && createPortal(
          <div ref={popRef} className={styles.dropMenu} style={menuPos}>
            <button
              onClick={() => {
                setMenuOpen(false);
                onEdit(contact);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              Edit
            </button>
            {contact.source === "auto_call" && (
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onEdit({ ...contact, _promoteToContact: true });
                }}
                style={{ color: "#7B1FA2" }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                  <line x1="20" y1="8" x2="20" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <line x1="23" y1="11" x2="17" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Move to Contacts
              </button>
            )}
            {contact.source !== "auto_call" && (
              <button
                className={contact.isVip ? styles.vipItemActive : styles.vipItem}
                onClick={() => {
                  setMenuOpen(false);
                  if (!contact.isVip && contact.isBlacklisted) {
                    // Marking blocked contact as VIP — warn
                    if (window.confirm("This contact is blocked. Marking as VIP will not auto-unblock. Unblock first from the contact menu.\n\nContinue marking as VIP?")) {
                      onEdit({ ...contact, _toggleVip: true });
                    }
                  } else {
                    onEdit({ ...contact, _toggleVip: true });
                  }
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <polygon
                    points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill={contact.isVip ? "#F59E0B" : "none"}
                    strokeLinejoin="round"
                  />
                </svg>
                {contact.isVip ? "Remove VIP" : "Mark VIP"}
              </button>
            )}
            {contact.isBlacklisted ? (
              <button
                className={styles.unblockItem}
                onClick={() => {
                  setMenuOpen(false);
                  onUnblock?.(contact);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Unblock
              </button>
            ) : (
              <button
                className={styles.blockItem}
                onClick={() => {
                  setMenuOpen(false);
                  onBlock(contact);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" stroke="currentColor" strokeWidth="2" />
                </svg>
                Block
              </button>
            )}
            {/* Delete is intentionally omitted here (handled by the top-level
                delete icon). The divider that used to sit above it went with
                it — as the last child it just drew a stray line, plus its
                4px margins, under the final menu item. */}
          </div>,
          document.body
        )}
      </div>
    </div>
  );
});

// ── Blacklist Card ────────────────────────────────────────────────────────────
const BlacklistCard = ({ entry, onUnblock, readOnly = false }) => (
  <div>
    <div className={styles.blacklistCard}>
      <div className={styles.blockIcon}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="#D63B3B" strokeWidth="2" />
          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" stroke="#D63B3B" strokeWidth="2" />
        </svg>
      </div>
      <div className={styles.contactInfo}>
        <div className={styles.contactName}>{entry.callerName || "Unknown"}</div>
        <div className={styles.contactPhone}>{entry.phone}</div>
        <div className={styles.contactMeta}>
          <span className={styles.badge} style={{ background: "#FFF0F0", color: "#D63B3B" }}>
            {entry.reason === "auto_spam" ? "Auto-blocked" : "Manual"}
          </span>
          {entry._agentName && (
            <span className={styles.badge} style={{ background: "#F3E5F5", color: "#7B1FA2" }}>
              {entry._agentName}
            </span>
          )}
          {entry.scope && !entry._agentName && (
            <span className={styles.badge} style={{ background: "#EEF2FF", color: "#4338CA" }}>
              {entry.scope === "agent" ? "This agent" : "All agents"}
            </span>
          )}
          {entry.spamStrikeCount > 0 && (
            <span className={styles.badge} style={{ background: "#FEF3C7", color: "#92400E" }}>
              Spam Strikes: {entry.spamStrikeCount}
            </span>
          )}
          {entry.autoBlacklistedAt && (
            <span className={styles.lastCall}>{formatLocalDT(entry.autoBlacklistedAt)}</span>
          )}
        </div>
      </div>
      <button
        className={styles.unblockBtn}
        onClick={() => onUnblock(entry)}
        style={readOnly ? { display: "none" } : undefined}
      >
        Unblock
      </button>
    </div>
  </div>
);

// ── Bulk "Move to category" pill + popover ──────────────────────────────────
//
// Sits in the My Contacts bulk toolbar alongside Mark VIP / Block / Delete.
// Visually matches the sibling pills (same height, padding, border-radius,
// purple-on-lavender) — we couldn't reuse a native <select> because the
// browser-default chrome (focus ring, dropdown arrow, options styling)
// breaks the rhythm of the other custom buttons.
//
// The popover is rendered via createPortal to document.body and positioned
// with `position: fixed` against the button's bounding rect. Earlier this
// was a plain `position: absolute` child, but the parent bulk toolbar has
// `overflow-x: auto` (so the pill row can scroll horizontally on mobile),
// and that was clipping the dropdown — clicking the button registered but
// the menu never appeared. Portalling out of the toolbar fixes it without
// touching the toolbar's overflow rule.
//
// Outside-click + Escape close the menu so it behaves like a real popover.
const BulkMoveCategoryMenu = ({ count, categories, onPick }) => {
  const [open, setOpen] = useState(false);
  const [menuRect, setMenuRect] = useState(null);
  const buttonRef = useRef();
  const menuRef = useRef();

  // Compute popover position from the button's bounding rect each time
  // it opens, so layout shifts (toolbar scroll, viewport resize) don't
  // strand the menu mid-air. Re-measured on scroll/resize while open.
  const updateRect = () => {
    if (!buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    setMenuRect({
      top: r.bottom + 4,
      left: r.left,
    });
  };

  useEffect(() => {
    if (!open) return;
    updateRect();
    const onDocClick = (e) => {
      const inButton = buttonRef.current && buttonRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inButton && !inMenu) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScrollOrResize = () => updateRect();
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={`Move ${count} contact(s) to a category`}
        style={{
          padding: "4px 10px",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          background: "#F4ECFF",
          color: "#6524EB",
          border: "1px solid #6524EB",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          fontFamily: "inherit",
          flex: "0 0 auto",
        }}
      >
        Move to
        <span aria-hidden="true" style={{ fontSize: 10, marginTop: 1 }}>
          ▾
        </span>
      </button>
      {open && menuRect && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: "fixed",
            top: menuRect.top,
            left: menuRect.left,
            minWidth: 160,
            maxHeight: 240,
            overflowY: "auto",
            background: "#fff",
            border: "1px solid #E0E5F2",
            borderRadius: 10,
            boxShadow: "0 8px 24px -8px rgba(15, 23, 42, 0.18)",
            padding: 4,
            zIndex: 10000,
          }}
        >
          {(categories || []).length === 0 ? (
            <div
              style={{
                padding: "8px 10px",
                fontSize: 12,
                color: "#6E6A7C",
              }}
            >
              No categories yet
            </div>
          ) : (
            categories.map((cat) => (
              <button
                key={cat}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onPick(cat);
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: "none",
                  background: "transparent",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#24252C",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  textTransform: "capitalize",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#F4ECFF";
                  e.currentTarget.style.color = "#6524EB";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#24252C";
                }}
              >
                {cat}
              </button>
            ))
          )}
        </div>,
        document.body
      )}
    </>
  );
};

// ── Agent Contact Card ───────────────────────────────────────────────────────
const AgentContactCard = ({ ac, bulkMode, isSelected, onSelectToggle, onBlock, onUnblock, onMoveToContacts, onDelete, readOnly = false }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef();
  // The portalled menu node — needed so outside-click can still tell "inside
  // the menu" from "outside" once the menu leaves the card's subtree.
  const popRef = useRef();
  const menuPos = useAnchoredMenuStyle(menuRef, menuOpen);

  useEffect(() => {
    const h = (e) => {
      const inAnchor = menuRef.current && menuRef.current.contains(e.target);
      const inMenu = popRef.current && popRef.current.contains(e.target);
      if (!inAnchor && !inMenu) setMenuOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div
      className={`${styles.contactCard} ${ac.isBlocked ? styles.contactCardBlocked : ""}`}
      style={menuOpen ? { position: "relative", zIndex: 100 } : {}}
    >
      <div className={styles.acCardRow}>
        <div className={styles.avtarDiv}>
          {bulkMode && (
            <input type="checkbox" checked={isSelected} onChange={onSelectToggle} className={styles.bulkCheckbox} />
          )}
          {ac.isBlocked ? (
            <div className={styles.blockIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#D63B3B" strokeWidth="2" />
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" stroke="#D63B3B" strokeWidth="2" />
              </svg>
            </div>
          ) : (
            <Avatar name={ac.contactName || ac.phone} size={40} />
          )}

        </div>
        <div className={styles.acCardInfo}>
          <div className={styles.acCardName}>
            {ac.contactName || ac.phone}
            {ac.isVip && <span className={styles.acCardVip}>&#9733; VIP</span>}
          </div>
          <div className={styles.acCardPhone}>
            {ac.phone}
            {ac._agentName && <span className={styles.acCardAgentBadge}>{ac._agentName}</span>}
          </div>
          <div className={styles.acCardMeta}>
            <span className={styles.callsDiv}>Calls: {ac.callCount || 0}</span>
            {ac.spamStrikeCount > 0 && <span className={styles.acCardSpam}>Spam Strikes: {ac.spamStrikeCount}</span>}

          </div>
          {ac.isBlocked && ac.blockedAt
            ? <span className={styles.acCardMeta2}>Blocked: {formatLocalDT(ac.blockedAt)}</span>
            : ac.lastCallAt && <span className={styles.acCardMeta2}>Last: {formatLocalDT(ac.lastCallAt)}</span>
          }
          <div className={styles.acCardBadges}>
            {ac.isBlocked && <span className={styles.blockedBadge}>{ac.blockedReason === "auto_spam" ? "Auto Blocked" : "Manual Blocked"}</span>}
            {ac.isGlobalBlocked && <span className={styles.acCardGlobalBlock}>Global Block</span>}
            {ac.contactCategory && <CategoryBadge cat={ac.contactCategory} />}
          </div>
        </div>
      </div>

      {/* {!ac._hideDelete && (
        <div
          onClick={() => onDelete?.(ac)}
          style={readOnly ? { display: "none" } : { cursor: "pointer" }}
        >
          <img src="/svg/delete-icon3.svg" alt="delete" />
        </div>
      )} */}
      <div
        className={styles.cardActions}
        ref={menuRef}
        style={readOnly ? { display: "none" } : undefined}
      >
        <button className={styles.menuBtn} onClick={() => setMenuOpen(p => !p)}>
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.5" y="0.5" width="29" height="29" rx="9.5" fill="white" stroke="#E8E9EE" />
            <circle cx="8" cy="15" r="2" fill="#24252C" />
            <circle cx="15" cy="15" r="2" fill="#24252C" />
            <circle cx="22" cy="15" r="2" fill="#24252C" />
          </svg>
        </button>
        {menuOpen && menuPos && createPortal(
          <div ref={popRef} className={styles.dropMenu} style={menuPos}>
            {!ac._isInMyContacts && !ac._blacklistMode && (
              <button onClick={() => { setMenuOpen(false); onMoveToContacts?.(ac); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                  <line x1="20" y1="8" x2="20" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <line x1="23" y1="11" x2="17" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Save to My Contacts
              </button>
            )}
            {ac.isBlocked ? (
              <button className={styles.unblockItem} onClick={() => { setMenuOpen(false); onUnblock?.(ac); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Unblock
              </button>
            ) : (
              <button className={styles.blockItem} onClick={() => { setMenuOpen(false); onBlock?.(ac); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" stroke="currentColor" strokeWidth="2" />
                </svg>
                Block
              </button>
            )}
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const ContactManager = ({
  agentId,
  agents = STABLE_EMPTY_AGENTS,
  contacts: contactsProp = STABLE_EMPTY_CONTACTS,
  blacklist: blacklistProp = STABLE_EMPTY_BLACKLIST,
  routingEnabled: routingProp = false,
  forwardPhone: forwardProp = "",
  onCreateContact,
  onUpdateContact,
  onDeleteContact,
  onBulkDeleteContacts,
  onBlockContact,
  onUnblockContact,
  onSync,
  onAddBlacklist,
  onRemoveBlacklist,
  onForwardSave,
  onRoutingToggle,
  onAgentSettingsSave,
  onAgentSettingsLoad,
  onTriggerGoogleSync,
  // Onboarding-only flags. Default off so the dashboard mount is
  // unaffected. ContactSyncStep flips `autoOpenSync` to pop the Sync
  // bottom-sheet on first paint and listens for `onSyncDialogClose`
  // to fire its intro.js tour after the sheet is dismissed.
  autoOpenSync = false,
  onSyncDialogClose,
  // When set (e.g. "/contact-sync"), the Google OAuth redirect lands
  // directly on this path instead of the default /contacts.
  oauthReturnTo,
  // Whether the Sync option may be shown — see `canSync` below.
  allowSync,
  // Support team members get contacts read-only — backend enforces it too.
  readOnly = false,
  // Agent contacts ke liye default agent — host ke paas id hoti hai bhale hi
  // agents ki list na bane (team member ke apne agent nahi hote).
  defaultAgentId = "",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;

  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") || "contacts";
  });
  const {
    contacts,
    contactsTotal,
    fetchAndSyncContacts,
    updateSingleContactLocally,
    updateMultipleContactsLocally,
    removeContactsLocally,
    addContactLocally
  } = useContactStore();

  const catScrollRef = useRef(null);
  const bulkScrollRef = useRef(null);

  useEffect(() => {
    const handleWheel = (e) => {
      // Agar vertical scroll (wheel) ho raha hai
      if (e.deltaY !== 0) {
        e.preventDefault(); // Main page ka scroll rokein
        e.currentTarget.scrollLeft += e.deltaY; // Jis element par scroll kiya, sirf usko horizontal scroll karein
      }
    };

    const catContainer = catScrollRef.current;
    const bulkContainer = bulkScrollRef.current;

    // Dono par listener lagayein
    if (catContainer) {
      catContainer.addEventListener("wheel", handleWheel, { passive: false });
    }
    if (bulkContainer) {
      bulkContainer.addEventListener("wheel", handleWheel, { passive: false });
    }

    // Cleanup function
    return () => {
      if (catContainer) {
        catContainer.removeEventListener("wheel", handleWheel);
      }
      if (bulkContainer) {
        bulkContainer.removeEventListener("wheel", handleWheel);
      }
    };
  }, [tab]); // tab dependency zaroori h taaki tab change hone par ref wapas attach ho sake

  useEffect(() => {
    fetchAndSyncContacts();
  }, [fetchAndSyncContacts]);

  useEffect(() => {
    if (contactsProp && contactsProp.length > 0) {
      useContactStore.getState().setContacts(contactsProp);
    }
  }, [contactsProp]);

  const [blacklist, setBlacklist] = useState(blacklistProp);
  const [search, setSearch] = useState("");
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsHasMore, setContactsHasMore] = useState(false);
  const [contactsLoadingMore, setContactsLoadingMore] = useState(false);

  // Advanced filters state
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [advFilters, setAdvFilters] = useState({
    all: true,
    vip: false,
    nonVip: false,
    blocked: false,
    nonBlocked: false,
  });

  const [catFilter, setCatFilter] = useState("all");
  const [routingEnabled, setRoutingEnabled] = useState(routingProp);
  const [forwardPhone, setForwardPhone] = useState(forwardProp);

  // Agent list used by Agent Contacts tab
  //
  // Sync pulls contacts out of the signed-in person's OWN Google/device
  // account, so it only makes sense when the contacts being written are their
  // own. The host passes `allowSync`:
  //   - account owner                       → true
  //   - admin viewing THEIR OWN contacts    → true (their agent, their account)
  //   - admin viewing an OWNER's company    → false (never push their personal
  //     contacts into someone else's account)
  // Falls back to "owner only" when the prop isn't supplied.
  const isTeamMemberCM =
    typeof window !== "undefined" && !!localStorage.getItem("teamOwnerId");
  const canSync = allowSync !== undefined ? !!allowSync : !isTeamMemberCM;

  // Modal open states — all controlled via isOpen boolean
  const [showSync, setShowSync] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [showAgentSheet, setShowAgentSheet] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);

  // Onboarding-only: pop the Sync bottom-sheet on first paint when
  // ContactSyncStep mounts us with `autoOpenSync`. Ref-guarded so the
  // sheet only auto-opens once per mount — if the user closes it then
  // taps elsewhere that toggles autoOpenSync, we don't re-open behind
  // their back. StrictMode-safe (refs persist across the dev double-
  // mount, state doesn't).
  const autoOpenedSyncRef = useRef(false);
  useEffect(() => {
    // Never auto-pop the Sync sheet when syncing isn't allowed here.
    if (autoOpenSync && !autoOpenedSyncRef.current && canSync) {
      autoOpenedSyncRef.current = true;
      setShowSync(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenSync]);
  const fabRef = useRef();
  const [successMsg, setSuccessMsg] = useState("");
  const [importResult, setImportResult] = useState(null);
  const showSuccess = (msg, duration = 3000) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(""), duration); };

  // Spam state
  const [agentSpamStates, setAgentSpamStates] = useState({});
  const [spamLoading, setSpamLoading] = useState(false);
  // Confirm dialog: ask before turning OFF all agents
  const [showSpamOffConfirm, setShowSpamOffConfirm] = useState(false);

  // Bulk
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Dynamic categories
  const [dynamicCategories, setDynamicCategories] = useState(null);
  const [currentIndustry, setCurrentIndustry] = useState("general");
  // const [industryTemplates, setIndustryTemplates] = useState([]);

  // Generic confirm dialog
  const [confirmState, setConfirmState] = useState(null);

  // Agent picker: ask which agents to enable when turning ON
  const [showAgentPicker, setShowAgentPicker] = useState(false);

  // ── NEW: Agent Contacts (per-agent tracking) ────────────────────────────
  const [acSelectedAgent, setAcSelectedAgent] = useState(defaultAgentId || "all");
  const [acList, setAcList] = useState([]);
  const [acLoading, setAcLoading] = useState(false);
  const [acError, setAcError] = useState(null);
  const [acFilter, setAcFilter] = useState("all"); // "all" | "blocked" | "watchlist"
  const [acSearch, setAcSearch] = useState("");
  const [acSummary, setAcSummary] = useState({ total: 0, blocked: 0, watchList: 0 });
  const [acPage, setAcPage] = useState(1);
  const [acHasMore, setAcHasMore] = useState(false);
  // Bulk select for Agent Contacts
  const [acBulkMode, setAcBulkMode] = useState(false);
  const [acSelectedIds, setAcSelectedIds] = useState(new Set());

  // Blacklist tab agent filter
  const [blSelectedAgent, setBlSelectedAgent] = useState("all");
  const [blReasonFilter, setBlReasonFilter] = useState("total"); // "total" | "auto_spam" | "manual"

  // Reload My Contacts from API (page 1 — resets list)
  const reloadContacts = async () => {
    await fetchAndSyncContacts(true); // force reload
  };

  // Load next page of My Contacts (append)
  const loadMoreContacts = async () => {
    // Deprecated — virtualization used instead
  };

  const masterSpamOn = Object.values(agentSpamStates).some((s) => s?.autoBlacklistEnabled === true);

  // ── Load Agent Contacts when agent/tab/filter changes ───────────────────
  const loadAgentContacts = async (aid, page = 1, append = false) => {
    setAcLoading(true);
    try {
      const params = { page, limit: 200 };
      if (aid && aid !== "all") params.agentId = aid;
      if (acFilter === "blocked") params.isBlocked = true;
      if (acFilter === "watchlist") params.watchList = true;
      if (acSearch) params.search = acSearch;

      const data = await fetchAgentContacts(params);
      const agentMap = {};
      agents.forEach(a => { agentMap[a.agent_id] = a.agentName || a.agent_id; });
      const newItems = (data.agentContacts || []).map(ac => ({ ...ac, _agentName: agentMap[ac.agentId] || ac.agentId }));
      if (append) {
        setAcList(prev => [...prev, ...newItems]);
      } else {
        setAcList(newItems);
      }
      setAcPage(page);
      setAcHasMore(newItems.length >= 200);

      const summaryParams = {};
      if (aid && aid !== "all") summaryParams.agentId = aid;
      // agentContactsSummary expects agentId as first arg
      const summary = await fetchAgentContactsSummary(aid === "all" ? undefined : aid);
      setAcSummary(summary);
      setAcError(null);
    } catch (e) {
      console.error("[AgentContacts] load failed:", e);
      setAcList([]);
      // Pehle sirf khaali list dikhti thi — permission na hone par bhi lagta tha
      // ki koi contact hai hi nahi.
      setAcError(
        e?.response?.data?.error || e?.message || "Could not load agent contacts",
      );
    }
    setAcLoading(false);
  };

  const loadBlacklist = async () => {
    try {
      const data = await fetchBlacklist(blSelectedAgent === "all" ? undefined : blSelectedAgent);
      setBlacklist(Array.isArray(data) ? data : []);
    } catch { }
  };

  // Load agent contacts on mount + when tab/agent/filter changes
  useEffect(() => {
    loadAgentContacts(acSelectedAgent);
  }, [acSelectedAgent, acFilter]);

  useEffect(() => {
    if (tab === "blacklist") loadBlacklist();
  }, [tab, blSelectedAgent]);

  // ?tab=blacklist se bhi support wahan na pahunch jaye.
  useEffect(() => {
    if (readOnly && tab === "blacklist") setTab("contacts");
  }, [readOnly, tab]);

  useEffect(() => {
    if (!agents.length || !onAgentSettingsLoad) return;
    const loadAll = async () => {
      setSpamLoading(true);
      const results = {};
      await Promise.all(
        agents.map(async (a) => {
          try {
            const data = await onAgentSettingsLoad(a.agent_id);
            results[a.agent_id] = data || {};
          } catch {
            results[a.agent_id] = {};
          }
        })
      );
      setAgentSpamStates(results);
      setSpamLoading(false);
    };
    loadAll();
  }, [agents]);

  const enableAgentSpam = async (agentIds) => {
    for (const aid of agentIds) {
      const current = agentSpamStates[aid] || {};
      const updated = { ...current, autoBlacklistEnabled: true };
      try {
        await onAgentSettingsSave?.(aid, updated);
        setAgentSpamStates((prev) => ({ ...prev, [aid]: updated }));
      } catch (e) {
        console.error("[MasterSpam] enable error:", e);
      }
    }
  };

  const disableAllSpam = async () => {
    for (const a of agents) {
      const current = agentSpamStates[a.agent_id] || {};
      const updated = { ...current, autoBlacklistEnabled: false };
      try {
        await onAgentSettingsSave?.(a.agent_id, updated);
        setAgentSpamStates((prev) => ({ ...prev, [a.agent_id]: updated }));
        console.log("[MasterSpam] disabled for:", a.agent_id);
      } catch (e) {
        console.error("[MasterSpam] disable error:", e);
      }
    }
    setShowSpamOffConfirm(false);
    showSuccess("Auto spam blocking disabled for all agents");
  };

  // Check if master push is enabled — warn if not
  const checkMasterPush = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return true;
      const userObj = decodeToken(token);
      const userId = userObj?.id || userObj?._id;
      if (!userId) return true;
      const res = await fetch(`${API_BASE_URL}/push/status/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        return data.pushEnabled;
      }
    } catch { }
    return true; // assume enabled on error
  };

  const pendingPushBusyRef = useRef(false);
  useEffect(() => {
    const applyPendingAgentPush = async () => {
      if (pendingPushBusyRef.current) return;

      const raw = sessionStorage.getItem("pendingAgentPush");
      if (!raw) return;

      let pending;
      try {
        pending = JSON.parse(raw);
      } catch {
        sessionStorage.removeItem("pendingAgentPush");
        return;
      }

      const aid = pending?.agentId;
      if (!aid) {
        sessionStorage.removeItem("pendingAgentPush");
        return;
      }

      pendingPushBusyRef.current = true;
      try {
        const pushOn = await checkMasterPush();

        // User never enabled Master Push (backed out) — discard, change nothing.
        if (!pushOn) {
          sessionStorage.removeItem("pendingAgentPush");
          return;
        }

        // Master Push is ON now — finish enabling this agent's push for real.
        const current =
          agentSpamStates[aid] ||
          (onAgentSettingsLoad ? await onAgentSettingsLoad(aid) : {}) ||
          {};
        const updated = { ...current, notifyPush: true };

        await onAgentSettingsSave?.(aid, updated);
        setAgentSpamStates((prev) => ({ ...prev, [aid]: updated }));
        showSuccess("Push notifications enabled for this agent.");
      } catch (e) {
        console.error("[PendingAgentPush] apply failed:", e);
      } finally {
        sessionStorage.removeItem("pendingAgentPush");
        pendingPushBusyRef.current = false;
      }
    };

    applyPendingAgentPush();

    const onVisible = () => {
      if (document.visibilityState === "visible") applyPendingAgentPush();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleMasterSpamToggle = async () => {
    if (agents.length === 0) return;
    if (masterSpamOn) {
      setShowSpamOffConfirm(true);
    } else {
      // Check if any agent has push notify ON — only then check master push
      const anyPushOn = Object.values(agentSpamStates).some(s => s?.notifyPush);
      if (anyPushOn) {
        const pushOn = await checkMasterPush();
        if (!pushOn) {
          setConfirmState({
            title: "Push Notifications Disabled",
            message: "Some agents have push notifications enabled but Master Push is OFF. Spam alerts won't reach your phone.\n\nEnable push in Profile?",
            confirmLabel: "Go to Profile",
            cancelLabel: "Skip, Enable Spam",
            onConfirm: () => {
              setConfirmState(null);
              navigate("/edit-profile", {
                state: {
                  returnTo,
                },
              });
            },
            onCancel: () => {
              setConfirmState(null);
              if (agents.length === 1) {
                enableAgentSpam([agents[0].agent_id]);
              } else {
                setShowAgentPicker(true);
              }
            },
          });
          return;
        }
      }
      if (agents.length === 1) {
        enableAgentSpam([agents[0].agent_id]);
      } else {
        setShowAgentPicker(true);
      }
    }
  };

  // ── Sync props ───────────────────────────────────────────────────────────────
  // Only sync if parent actually fetched new data (different ref)
  const contactsPropRef = useRef(contactsProp);
  useEffect(() => {
    if (contactsProp !== contactsPropRef.current) {
      contactsPropRef.current = contactsProp;
      useContactStore.getState().setContacts(contactsProp);
    }
  }, [contactsProp]);

  useEffect(() => {
    setBlacklist(blacklistProp);
  }, [blacklistProp]);

  // Watch list is now in Agent Contacts tab (filter: "Watch")
  // No longer fetched separately for Blacklist tab

  useEffect(() => {
    if (agentId) {
      getAgentCategories(agentId)
        .then((data) => {
          // setDynamicCategories(data.categories || []);
          setDynamicCategories(["family", "friends", "colleagues", "customer", "other"]);
          setCurrentIndustry(data.industry || "general");
        })
        .catch(() => setDynamicCategories(["family", "friends", "colleagues", "customer", "other"]));
      // getIndustryTemplates()
      //   .then(setIndustryTemplates)
      //   .catch(() => setIndustryTemplates([]));
    }
  }, [agentId]);

  // ── RN WebSocket: auto-refresh contacts on sync success ─────────────────
  useEffect(() => {
    const handleRNSync = (event) => {
      try {
        const msg = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (!msg?.__fromRN) return;
        if (msg.type === "CONTACTS_API_SUCCESS" || msg.type === "CONTACTS_SYNCED") {
          onSync?.("refresh");
        }
      } catch { /* ignore parse errors */ }
    };
    window.addEventListener("message", handleRNSync);
    document.addEventListener("message", handleRNSync);
    return () => {
      window.removeEventListener("message", handleRNSync);
      document.removeEventListener("message", handleRNSync);
    };
  }, [onSync]);

  useEffect(() => {
    setRoutingEnabled(routingProp);
  }, [routingProp]);

  useEffect(() => {
    setForwardPhone(forwardProp);
  }, [forwardProp]);

  useEffect(() => {
    const h = (e) => {
      if (fabRef.current && !fabRef.current.contains(e.target)) setFabOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleRoutingToggle = () => {
    if (!routingEnabled && !forwardPhone) {
      setShowForwardModal(true);
    } else {
      const next = !routingEnabled;
      setRoutingEnabled(next);
      onRoutingToggle?.(next);
    }
  };

  const handleForwardSave = (phone) => {
    setForwardPhone(phone);
    setRoutingEnabled(true);
    onForwardSave?.(phone);
    onRoutingToggle?.(true);
  };

  // Split contacts by source for tabs
  const myContacts = contacts.filter((c) => c.source !== "auto_call");
  const agentContacts = contacts.filter((c) => c.source === "auto_call");

  // Filter based on active tab
  const activeList = tab === "contacts" ? myContacts : tab === "agent_contacts" ? agentContacts : [];

  const filteredContacts = useMemo(() => {
    return activeList.filter((c) => {
      const qs = search.toLowerCase();
      const matchSearch =
        !search ||
        c.name?.toLowerCase().includes(qs) ||
        c.phone?.includes(search);
      const matchCat = catFilter === "all" ? true : c.category === catFilter;

      // Advanced Filter overrides
      let matchAdvVip = true;
      if (!advFilters.all) {
        if (advFilters.vip && advFilters.nonVip) matchAdvVip = true;
        else if (advFilters.vip) matchAdvVip = c.isVip;
        else if (advFilters.nonVip) matchAdvVip = !c.isVip;
      }

      let matchAdvBlocked = true;
      if (!advFilters.all) {
        if (advFilters.blocked && advFilters.nonBlocked) matchAdvBlocked = true;
        else if (advFilters.blocked) matchAdvBlocked = c.isBlacklisted;
        else if (advFilters.nonBlocked) matchAdvBlocked = !c.isBlacklisted;
      }

      return matchSearch && matchCat && matchAdvVip && matchAdvBlocked;
    }).sort((a, b) => {
      // Agent contacts + blacklist: latest first (by lastCallAt/createdAt)
      if (tab === "agent_contacts" || tab === "blacklist") {
        const dateA = new Date(a.lastCallAt || a.createdAt || 0).getTime();
        const dateB = new Date(b.lastCallAt || b.createdAt || 0).getTime();
        return dateB - dateA;
      }
      // My Contacts: A→Z, numbers at end
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      const aStartsWithLetter = /^[a-z]/i.test(nameA);
      const bStartsWithLetter = /^[a-z]/i.test(nameB);
      if (aStartsWithLetter && !bStartsWithLetter) return -1;
      if (!aStartsWithLetter && bStartsWithLetter) return 1;
      return nameA.localeCompare(nameB);
    });
  }, [activeList, search, catFilter, tab]);

  const activeCats = dynamicCategories || ["family", "friends", "colleagues", "customer", "other"];
  const activeCategories = ["all", ...activeCats];


  return (
    <div className={styles.page}>
      {/* ── TABS ── */}
      <div className={styles.tabBar} data-tour="tabs">
        <button
          className={`${styles.tab} ${tab === "contacts" ? styles.tabActive : ""}`}
          onClick={() => setTab("contacts")}
          data-tour="my-contacts-tab"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path
              d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
            <path
              d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          My Contacts
          <span className={styles.tabCount}>{myContacts.length}</span>
        </button>
        <button
          className={`${styles.tab} ${tab === "agent_contacts" ? styles.tabActive : ""}`}
          onClick={() => setTab("agent_contacts")}
          data-tour="agent-contacts-tab"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M12 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" stroke="currentColor" strokeWidth="2" />
            <path d="M19 9a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" stroke="currentColor" strokeWidth="2" />
            <path d="M5 9a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" stroke="currentColor" strokeWidth="2" />
            <path d="M16 21v-2a4 4 0 0 0-8 0v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Agent Contacts
          <span className={styles.tabCount} style={{ background: "#F3E5F5", color: "#7B1FA2" }}>
            {acSummary.total}
          </span>
        </button>
        {/* Blacklist poori tarah block/unblock ka kaam hai — support ke liye band. */}
        <button
          className={`${styles.tab} ${tab === "blacklist" ? styles.tabActive : ""}`}
          onClick={() => setTab("blacklist")}
          data-tour="blacklist-tab"
          style={readOnly ? { display: "none" } : undefined}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" stroke="currentColor" strokeWidth="2" />
          </svg>
          Blacklist
          <span className={styles.tabCount} style={{ background: "#FFF0F0", color: "#D63B3B" }}>
            {blacklist.length}
          </span>
        </button>
      </div>

      {/* Success Banner */}
      {successMsg && (
        <div style={{
          padding: "10px 16px", background: "#E8F5E9", color: "#2E7D32",
          fontSize: 13, fontWeight: 600, textAlign: "center",
          borderBottom: "1px solid #C8E6C9", animation: "fadeIn 0.2s",
        }}>
          {successMsg}
        </div>
      )}

      {/* ════ CONTACTS TAB ════ */}
      {tab === "contacts" && (
        <div className={styles.tabContent}>
          {/* Routing toggle */}
          <div className={styles.toggleRow}>
            <div className={styles.toggleLeft}>
              <span className={styles.toggleLabel}>Contact Routing</span>
              <InfoTooltip text="When enabled, calls from your saved contacts will be forwarded directly to your phone." />
              <span className={styles.toggleDesc}>
                {forwardPhone ? (
                  <>
                    Forward to <strong>{forwardPhone}</strong>
                  </>
                ) : (
                  "Forward known callers directly to you"
                )}
              </span>
            </div>
            {/* Forwarding badalna bhi likhna hai — support ke liye band. */}
            <div
              onClick={() => setShowForwardModal(true)}
              style={readOnly ? { display: "none" } : undefined}
            >
              <img src="/svg/edit-icon3.svg" alt="edit-icon3" />
            </div>
            <div
              className={`${styles.toggle} ${routingEnabled ? styles.toggleOn : ""}`}
              onClick={handleRoutingToggle}
              style={readOnly ? { display: "none" } : undefined}
            >
              <div className={styles.toggleThumb} />
            </div>
          </div>

          {/* Search */}
          <div className={styles.searchRow}>
            <div className={styles.searchBox}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="8" stroke="#aaa" strokeWidth="2" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="#aaa" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or phone..."
                className={styles.searchInput}
              />
              {search && (
                <button className={styles.clearSearch} onClick={() => setSearch("")}>
                  ✕
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilterModal(true)}
              style={{
                marginLeft: 12,
                background: advFilters.all ? "transparent" : "#6524eb",
                color: advFilters.all ? "#555" : "#fff",
                border: advFilters.all ? "1px solid #ddd" : "none",
                borderRadius: 8,
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
            </button>
          </div>

          {/* Category chips */}
          <div className={styles.catScroll}
            ref={catScrollRef} >
            {activeCategories.map((cat) => (
              <button
                key={cat}
                className={`${styles.catChip} ${catFilter === cat ? styles.catChipActive : ""}`}
                onClick={() => setCatFilter(cat)}
              >
                {cat.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                {cat !== "all" &&
                  !["family", "friends", "colleagues", "customer", "other", "blocked"].includes(cat) && (
                    <span
                      className={styles.catRemove}
                      onClick={(e) => {
                        e.stopPropagation();
                        const catName = cat.replace(/_/g, " ");
                        setConfirmState({
                          title: "Remove Category",
                          message: `Remove "${catName}" from categories? Contacts with this category won't lose it, but the filter chip will be hidden.`,
                          confirmLabel: "Remove",
                          danger: true,
                          onConfirm: () => {
                            const newCats = activeCats.filter((c) => c !== cat);
                            setDynamicCategories(newCats);
                            if (catFilter === cat) setCatFilter("all");
                            if (agentId)
                              updateAgentCategories(agentId, { categories: newCats }).catch(
                                console.error
                              );
                            setConfirmState(null);
                          },
                        });
                      }}
                    >
                      &#10005;
                    </span>
                  )}
              </button>
            ))}
            {!readOnly && (
              <button
                className={styles.catSettingsBtn}
                onClick={() => setShowAddCategory(true)}
                title="Add custom category"
              >
                +
              </button>
            )}
          </div>

          {/* Bulk action bar — read-only me sab actions write hi hain. */}
          <div
            className={styles.bulkBar}
            ref={bulkScrollRef}
            style={readOnly ? { display: "none" } : undefined}
          >
            <button
              className={`${styles.bulkToggle} ${bulkMode ? styles.bulkToggleActive : ""}`}
              onClick={() => {
                setBulkMode((p) => !p);
                setSelectedIds(new Set());
              }}
            >
              {bulkMode ? "Cancel" : "Select"}
            </button>
            {bulkMode && (
              <>
                <button
                  className={styles.bulkSelectAll}
                  onClick={() => {
                    const limit = Math.min(filteredContacts.length, 500);
                    if (selectedIds.size > 0 && selectedIds.size === limit) {
                      setSelectedIds(new Set());
                    } else {
                      setSelectedIds(new Set(filteredContacts.slice(0, 500).map((c) => c.id)));
                    }
                  }}
                >
                  {selectedIds.size > 0 && selectedIds.size === Math.min(filteredContacts.length, 500) ? "Deselect all" : "Select all"}
                </button>
                <span className={styles.bulkCount}>{selectedIds.size} selected</span>
                {selectedIds.size > 0 && (() => {
                  const selectedContacts = contacts.filter(c => selectedIds.has(c.id));
                  const allBlocked = selectedContacts.every(c => c.isBlacklisted);
                  const noneBlocked = selectedContacts.every(c => !c.isBlacklisted);
                  const anyBlocked = selectedContacts.some(c => c.isBlacklisted);
                  const allVip = selectedContacts.every(c => c.isVip);
                  const noneVip = selectedContacts.every(c => !c.isVip);
                  return (
                    <>
                      <button
                        className={styles.bulkVipBtn}
                        disabled={allVip}
                        style={{
                          opacity: allVip ? 0.5 : 1, // Disabled state me thoda fade dikhne ke liye
                          cursor: allVip ? "not-allowed" : "pointer"
                        }}
                        onClick={async () => {
                          try {
                            const ids = [...selectedIds];
                            if (ids.length === 0) return;
                            await apiBulkUpdateContacts(ids, { isVip: true });
                            updateMultipleContactsLocally(ids, { isVip: true });
                            setSelectedIds(new Set());
                            setBulkMode(false);
                          } catch (e) {
                            console.error("Bulk VIP failed:", e);
                          }
                        }}
                      >
                        Mark VIP
                      </button>
                      <button
                        className={styles.bulkUnvipBtn}
                        disabled={noneVip}
                        style={{
                          opacity: noneVip ? 0.5 : 1, // Disabled state me thoda fade dikhne ke liye
                          cursor: noneVip ? "not-allowed" : "pointer"
                        }}
                        onClick={async () => {
                          try {
                            const ids = [...selectedIds];
                            if (ids.length === 0) return;
                            await apiBulkUpdateContacts(ids, { isVip: false });
                            updateMultipleContactsLocally(ids, { isVip: false });
                            setSelectedIds(new Set());
                            setBulkMode(false);
                          } catch (e) {
                            console.error("Bulk unVIP failed:", e);
                          }
                        }}
                      >
                        Remove VIP
                      </button>
                      <button
                        className={styles.bulkDeleteBtn}
                        disabled={allBlocked}
                        style={{ background: "#FFF0F0", color: allBlocked ? "#ccc" : "#D63B3B", border: `1px solid ${allBlocked ? "#ddd" : "#D63B3B"}`, cursor: allBlocked ? "not-allowed" : "pointer" }}
                        onClick={() => {
                          const phones = selectedContacts.map(c => c.phone).filter(Boolean);
                          setConfirmState({
                            title: "Block for ALL Agents",
                            message: `Block ${selectedIds.size} contact(s) for ALL agents? They won't be able to reach any of your agents.`,
                            confirmLabel: "Block All",
                            danger: true,
                            onConfirm: async () => {
                              try {
                                const ids = [...selectedIds];
                                if (ids.length === 0) return;
                                await apiBulkUpdateContacts(ids, { isBlacklisted: true });
                                updateMultipleContactsLocally(ids, { isBlacklisted: true });
                                setSelectedIds(new Set());
                                setBulkMode(false);
                                showSuccess(`${ids.length} contact(s) blocked for all agents`);
                              } catch (e) { console.error("Bulk block failed:", e); }
                              setConfirmState(null);
                            },
                          });
                        }}
                      >
                        Block All
                      </button>
                      <button
                        disabled={noneBlocked}
                        style={{ padding: "4px 10px", borderRadius: 6, fontSize: 12, cursor: noneBlocked ? "not-allowed" : "pointer", background: noneBlocked ? "#f5f5f5" : "#E8F5E9", color: noneBlocked ? "#ccc" : "#2E7D32", border: `1px solid ${noneBlocked ? "#ddd" : "#4CAF50"}` }}
                        onClick={async () => {
                          try {
                            const ids = [...selectedIds];
                            if (ids.length === 0) return;
                            await apiBulkUpdateContacts(ids, { isBlacklisted: false });
                            updateMultipleContactsLocally(ids, { isBlacklisted: false });
                            setSelectedIds(new Set());
                            setBulkMode(false);
                            showSuccess(`${ids.length} contact(s) unblocked`);
                          } catch (e) { console.error("Bulk unblock failed:", e); }
                        }}
                      >
                        Unblock All
                      </button>
                      <button
                        className={styles.bulkDeleteBtn}
                        onClick={() => {
                          setConfirmState({
                            title: "Delete Contacts",
                            message: `Permanently delete ${selectedIds.size} contact(s)? This action cannot be undone.`,
                            confirmLabel: "Delete",
                            danger: true,
                            onConfirm: async () => {
                              try {
                                const ids = [...selectedIds];
                                if (ids.length === 0) return;
                                if (onBulkDeleteContacts) {
                                  await onBulkDeleteContacts(ids);
                                } else {
                                  await apiBulkDeleteAgentContacts({ ids });
                                }
                                removeContactsLocally(ids);
                                setSelectedIds(new Set());
                                setBulkMode(false);
                              } catch (e) {
                                console.error("Bulk delete failed:", e);
                              }
                              setConfirmState(null);
                            },
                          });
                        }}
                      >
                        Delete
                      </button>
                      {/*
                         Bulk "Move to category" — custom button + popover
                         menu. We can't use a native <select> here because
                         it has browser-native chrome (arrow, focus ring,
                         option styling) that doesn't match the rest of
                         the bulk action pills. Implementation:
                           - styled <button> sized identical to siblings
                           - click toggles a small menu beneath
                           - menu lists active categories; click fires
                             apiBulkUpdateContacts + local update + clears
                             selection (matches every other bulk action's
                             post-action behaviour).
                         Outside-click closes the menu via a one-shot
                         listener attached on open.
                      */}
                      <BulkMoveCategoryMenu
                        count={selectedIds.size}
                        categories={activeCats}
                        onPick={async (targetCat) => {
                          const ids = [...selectedIds];
                          if (ids.length === 0 || !targetCat) return;
                          try {
                            await apiBulkUpdateContacts(ids, { category: targetCat });
                            updateMultipleContactsLocally(ids, { category: targetCat });
                            setSelectedIds(new Set());
                            setBulkMode(false);
                            showSuccess(
                              `${ids.length} contact(s) moved to ${targetCat}`,
                            );
                          } catch (err) {
                            console.error("Bulk move-category failed:", err);
                          }
                        }}
                      />
                    </>
                  );
                })()}
              </>
            )}
          </div>

          {/* List */}
          <div
            className={styles.listArea}
          // Removed onScroll logic since virtualization is used and we fetch all at once
          >
            {filteredContacts.length === 0 ? (
              <div className={styles.emptyState}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#ccc" strokeWidth="1.5" />
                  <circle cx="9" cy="7" r="4" stroke="#ccc" strokeWidth="1.5" />
                </svg>
                <p className={styles.emptyTitle}>No contacts found</p>
                <p className={styles.emptySub}>
                  {search ? "Try a different search" : "Add contacts using the + button below"}
                </p>
              </div>
            ) : (
              <FixedSizeList
                // Size the list to its content, capped at the available
                // viewport height. A fixed 600px always overflowed the
                // listArea (max-height: 100vh - 300px) on phones, forcing a
                // scrollbar even for a single contact. Now it only scrolls
                // when the list is actually longer than the visible area.
                height={Math.min(
                  filteredContacts.length * CONTACT_ROW_HEIGHT,
                  Math.max(window.innerHeight - 300, 200)
                )}
                width="100%"
                // Row box is itemSize - 16 (see the renderer below, which insets
                // each row by 8px top and bottom). A .contactCard measures 89px
                // with its name, phone and badge row, so itemSize:100 gave the
                // card only 84px and the 5px shortfall overflowed react-window's
                // outer div — which has inline overflow:auto, so a scrollbar
                // appeared on a single-contact list. 112 leaves 96px of row.
                itemSize={CONTACT_ROW_HEIGHT}
                itemCount={filteredContacts.length}
                itemData={{ contacts: filteredContacts, selectedIds, bulkMode, readOnly }}
              >
                {({ index, style, data }) => {
                  const c = data.contacts[index];
                  return (
                    <div style={{ ...style, top: `${parseFloat(style.top) + 8}px`, height: `${parseFloat(style.height) - 16}px`, padding: "0 8px" }} key={c.id}>
                      <ContactCard
                        contact={c}
                        readOnly={data.readOnly}
                        bulkMode={data.bulkMode}
                        isSelected={data.selectedIds.has(c.id)}
                        onSelectToggle={() => {
                          const isSelected = data.selectedIds.has(c.id);
                          if (!isSelected && data.selectedIds.size >= 500) {
                            showSuccess("Maximum 500 contacts can be selected at once.");
                            return;
                          }
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            isSelected ? next.delete(c.id) : next.add(c.id);
                            return next;
                          });
                        }}
                        onEdit={async (cTarget) => {
                          if (cTarget._toggleVip) {
                            const newVip = !cTarget.isVip;
                            try {
                              await apiBulkUpdateContacts([cTarget.id], { isVip: newVip });
                              updateSingleContactLocally(cTarget.id, { isVip: newVip });
                            } catch (e) {
                              console.error("VIP toggle failed:", e);
                            }
                            return;
                          }
                          if (cTarget._promoteToContact) {
                            try {
                              await onUpdateContact?.(cTarget.id, { source: "manual", category: "customer" });
                              updateSingleContactLocally(cTarget.id, { source: "manual", category: "customer" });
                              setTab("contacts");
                            } catch (e) {
                              console.error("Promote contact failed:", e);
                            }
                            return;
                          }
                          setEditContact(cTarget);
                          setShowAdd(true);
                        }}
                        onBlock={async (cTarget) => {
                          try {
                            if (onBlockContact) await onBlockContact({ id: cTarget.id });
                            else await apiBulkUpdateContacts([cTarget.id], { isBlacklisted: true });
                          } catch (e) { console.error(e); }
                          updateSingleContactLocally(cTarget.id, { isBlacklisted: true });
                        }}
                        onUnblock={async (cTarget) => {
                          try {
                            if (onUnblockContact) await onUnblockContact({ id: cTarget.id });
                            else await apiBulkUpdateContacts([cTarget.id], { isBlacklisted: false });
                          } catch (e) { console.error(e); }
                          updateSingleContactLocally(cTarget.id, { isBlacklisted: false });
                        }}
                        onDelete={(cTarget) => {
                          setConfirmState({
                            title: "Delete Contact",
                            message: `Permanently delete ${cTarget.name || cTarget.phone}? This action cannot be undone.`,
                            confirmLabel: "Delete",
                            danger: true,
                            onConfirm: async () => {
                              try {
                                if (onDeleteContact) await onDeleteContact(cTarget.id);
                              } catch (e) { console.error(e); }
                              removeContactsLocally([cTarget.id]);
                              setConfirmState(null);
                            },
                          });
                        }}
                      />
                    </div>
                  );
                }}
              </FixedSizeList>
            )}
            {contactsLoadingMore && (
              <div style={{ textAlign: "center", padding: "12px 0", color: "#999", fontSize: 13 }}>
                Loading more contacts...
              </div>
            )}
          </div>
        </div>
      )}

      {/* ════ AGENT CONTACTS TAB (NEW — per-agent tracking) ════ */}
      {tab === "agent_contacts" && (
        <div className={styles.tabContent}>
          <div className={styles.acInfoBanner}>
            Agent call logs appear here automatically. Move trusted numbers to My Contacts for VIP handling and to avoid spam flagging          </div>

          {/* Agent Selector */}
          <div className={styles.acTopRow} style={{ flexDirection: "column", alignItems: "stretch" }}>
            <select
              value={acSelectedAgent}
              onChange={(e) => setAcSelectedAgent(e.target.value)}
              className={styles.formInput2}
            >
              <option value="all">All Agents</option>
              {agents.map((a) => (
                <option key={a.agent_id} value={a.agent_id}>{a.agentName || a.agent_id}</option>
              ))}
            </select>
            {/* <div className={styles.acFilterBtns}>
              {[
                { key: "all", label: "All", count: acSummary.total },
                { key: "blocked", label: "Blocked", count: acSummary.blocked },
                { key: "watchlist", label: "Watch", count: acSummary.watchList },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setAcFilter(f.key)}
                  className={`${styles.acFilterBtn} ${acFilter === f.key ? styles.acFilterBtnActive : ""}`}
                >
                  {f.label} {f.count > 0 && <strong>({f.count})</strong>}
                </button>
              ))}
            </div> */}
          </div>

          {/* Select + Search row */}
          <div className={styles.acSelectRow} style={{ flexDirection: "column-reverse", alignItems: "flex-start" }}>
            <button
              onClick={() => { setAcBulkMode(!acBulkMode); setAcSelectedIds(new Set()); }}
              className={`${styles.acSelectBtn} ${acBulkMode ? styles.acSelectBtnActive : ""}`}
            >
              {acBulkMode ? "Cancel" : "Select"}
            </button>
            <div className={styles.searchBox} style={{ flex: 1, marginBottom: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="8" stroke="#999" strokeWidth="2" />
                <path d="M21 21l-4.35-4.35" stroke="#999" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                placeholder="Search by phone or name..."
                value={acSearch}
                onChange={(e) => setAcSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadAgentContacts(acSelectedAgent)}
                className={styles.searchInput}
              />
            </div>
          </div>

          {/* Bulk action bar */}
          {acBulkMode && acSelectedIds.size > 0 && (
            <div className={styles.acBulkBar}>
              <span className={styles.acBulkCount}>{acSelectedIds.size} selected</span>
              <button
                className={`${styles.acBulkBtn} ${styles.acBulkBlock}`}
                onClick={async () => {
                  try {
                    await apiBulkBlock([...acSelectedIds]);
                    showSuccess(`Blocked ${acSelectedIds.size} contact(s)`);
                    setAcBulkMode(false); setAcSelectedIds(new Set());
                    loadAgentContacts(acSelectedAgent); loadBlacklist();
                  } catch (e) { console.error(e); }
                }}
              >
                Block Selected
              </button>
              <button
                onClick={async () => {
                  try {
                    await apiBulkUnblock([...acSelectedIds]);
                    showSuccess(`Unblocked ${acSelectedIds.size} contact(s)`);
                    setAcBulkMode(false); setAcSelectedIds(new Set());
                    loadAgentContacts(acSelectedAgent); loadBlacklist();
                  } catch (e) { console.error(e); }
                }}
                className={`${styles.acBulkBtn} ${styles.acBulkUnblock}`}
              >
                Unblock Selected
              </button>
              <button
                className={`${styles.acBulkBtn} ${styles.acBulkMove}`}
                onClick={async () => {
                  try {
                    const res = await apiBulkPromote([...acSelectedIds]);
                    showSuccess(`${res.promoted || 0} moved to My Contacts`);
                    setAcBulkMode(false); setAcSelectedIds(new Set());
                    loadAgentContacts(acSelectedAgent);
                    reloadContacts();
                    // Alert if any were blocked
                    if (res.blockedPhones?.length > 0) {
                      const phoneList = res.blockedPhones.map(b => b.phone).join(", ");
                      setConfirmState({
                        title: "Some numbers are blocked",
                        message: `These moved numbers are still blocked on some agents: ${phoneList}.\nUnblock all?`,
                        confirmLabel: "Unblock All",
                        onConfirm: async () => {
                          for (const bp of res.blockedPhones) {
                            await apiUnblockAllAgents(bp.phone);
                          }
                          showSuccess("All unblocked");
                          loadAgentContacts(acSelectedAgent); loadBlacklist();
                          setConfirmState(null);
                        },
                      });
                    }
                  } catch (e) { console.error(e); }
                }}
                style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #7B1FA2", background: "#F3E5F5", color: "#7B1FA2", fontSize: 12, cursor: "pointer" }}
              >
                Move to My Contacts
              </button>
              {/* <button
                className={styles.acBulkBtn}
                style={{ border: "1px solid #D63B3B", background: "#fff", color: "#D63B3B" }}
                onClick={() => {
                  setConfirmState({
                    title: "Delete Agent Contacts",
                    message: `Permanently delete ${acSelectedIds.size} agent contact(s)? Call history will be lost.`,
                    confirmLabel: "Delete",
                    danger: true,
                    onConfirm: async () => {
                      try {
                        await apiBulkDeleteAgentContacts([...acSelectedIds]);
                        showSuccess(`Deleted ${acSelectedIds.size} contact(s)`);
                        setAcBulkMode(false); setAcSelectedIds(new Set());
                        loadAgentContacts(acSelectedAgent);
                      } catch (e) { console.error(e); }
                      setConfirmState(null);
                    },
                  });
                }}
              >
                Delete
              </button> */}
            </div>
          )}

          {/* List */}
          {acLoading ? (
            <div className={styles.emptyState}><p>Loading...</p></div>
          ) : acError ? (
            <div className={styles.emptyState}>
              <p>{acError}</p>
            </div>
          ) : acList.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No agent contacts yet.</p>
              <p style={{ fontSize: 12, color: "#999" }}>Contacts will appear here automatically after calls.</p>
            </div>
          ) : (
            <div
              className={styles.contactList}
              onScroll={(e) => {
                if (!acHasMore || acLoading) return;
                const { scrollTop, scrollHeight, clientHeight } = e.target;
                if (scrollHeight - scrollTop - clientHeight < 150) {
                  loadAgentContacts(acSelectedAgent, acPage + 1, true);
                }
              }}
            // style={{ maxHeight: "calc(100vh - 350px)", overflowY: "auto" }}
            >
              {acList.map((ac) => (
                <AgentContactCard
                  key={ac.id}
                  ac={ac}
                  readOnly={readOnly}
                  bulkMode={acBulkMode}
                  isSelected={acSelectedIds.has(ac.id)}
                  onSelectToggle={() => {
                    setAcSelectedIds(prev => {
                      const next = new Set(prev);
                      next.has(ac.id) ? next.delete(ac.id) : next.add(ac.id);
                      return next;
                    });
                  }}
                  onBlock={async (ac) => {
                    try {
                      await apiBlockAgentContact(ac.id, "manual");
                      const agentName = agents.find(a => a.agent_id === ac.agentId)?.agentName || "agent";
                      showSuccess(`Blocked for ${agentName}`);
                      loadAgentContacts(acSelectedAgent);
                      loadBlacklist();
                    } catch (e) { console.error(e); }
                  }}
                  onUnblock={async (ac) => {
                    try {
                      await apiUnblockAgentContact(ac.id);
                      showSuccess("Unblocked");
                      loadAgentContacts(acSelectedAgent);
                      loadBlacklist();
                    } catch (e) { console.error(e); }
                  }}
                  onMoveToContacts={async (ac) => {
                    try {
                      const info = await apiGetPromoteInfo(ac.id);
                      let infoMsg = "";
                      if (info.agentCount > 1) {
                        const agentNames = info.agents.map(a => a.agentName).join(", ");
                        infoMsg += `This number exists in ${info.agentCount} agent contact lists (${agentNames}). Moving will make it accessible to all agents.\n\n`;
                      }
                      if (info.nameConflicts) {
                        infoMsg += `Different names found: ${info.nameConflicts.join(", ")}. Suggested: "${info.suggestedName}"\n\n`;
                      }
                      if (info.blockedOnAgents?.length > 0) {
                        infoMsg += `Blocked on: ${info.blockedOnAgents.join(", ")}. You can unblock after saving.`;
                      }
                      if (info.existingContact && info.existingContact.source !== "auto_call") {
                        showSuccess("Already in My Contacts!");
                        return;
                      }
                      const openModal = () => {
                        const rawName = (info.suggestedName || ac.callerName || "").trim();
                        const phoneVal = (info.phone || ac.phone || "").trim();
                        // If rawName is empty, matches phone, or looks like a phone number, leave name blank
                        const isPhoneLike = !rawName || rawName === phoneVal || /^[\d\s+\-().]{6,}$/.test(rawName);
                        setEditContact({
                          _isPromote: true,
                          _agentContactId: ac.id,
                          phone: phoneVal,
                          name: isPhoneLike ? "" : rawName,
                          email: ac.contactEmail || "",
                          company: ac.contactCompany || "",
                          category: ac.contactCategory || "customer",
                          isVip: ac.isVip || false,
                        });
                        setShowAdd(true);
                      };
                      if (infoMsg) {
                        setConfirmState({
                          title: "Move to My Contacts",
                          message: infoMsg + "\n\nAll your agents will recognize this contact (VIP, name greeting, transfer).\n\nContinue?",
                          confirmLabel: "Continue",
                          onConfirm: () => { setConfirmState(null); openModal(); },
                        });
                      } else {
                        openModal();
                      }
                    } catch (e) { console.error(e); }
                  }}
                  onDelete={(ac) => {
                    setConfirmState({
                      title: "Delete Agent Contact",
                      message: `Delete ${ac.contactName || ac.phone} from agent contacts? Call history will be lost.`,
                      confirmLabel: "Delete",
                      danger: true,
                      onConfirm: async () => {
                        try {
                          await apiDeleteAgentContact(ac.id);
                          showSuccess("Deleted");
                          loadAgentContacts(acSelectedAgent);
                        } catch (e) { console.error(e); }
                        setConfirmState(null);
                      },
                    });
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ════ BLACKLIST TAB ════ */}
      {tab === "blacklist" && (
        <div className={styles.tabContent}>
          <div className={styles.toggleRow}>
            <div className={styles.toggleLeft}>

              <span className={styles.toggleLabel}>Auto Spam Blocking</span>
              <InfoTooltip text="Automatically block callers identified as spam by the AI agent." />
              <span className={styles.toggleDesc}>
                {spamLoading
                  ? "Loading agent settings..."
                  : masterSpamOn
                    ? `Active on ${Object.values(agentSpamStates).filter((s) => s?.autoBlacklistEnabled).length
                    } agent(s)`
                    : agents.length === 0
                      ? "No agents configured"
                      : "Block spam callers automatically"}
              </span>
            </div>
            <div
              className={`${styles.toggle} ${masterSpamOn ? styles.toggleOn : ""} ${spamLoading || agents.length === 0 ? styles.dimmed : ""
                }`}
              onClick={!spamLoading && agents.length > 0 ? handleMasterSpamToggle : undefined}
              style={spamLoading || agents.length === 0 ? { cursor: "not-allowed" } : { cursor: "pointer" }}
            >
              <div className={styles.toggleThumb} />
            </div>
          </div>

          <div className={styles.agentSettingsRow} data-tour="blacklist-settings">
            <button className={styles.agentSettingsBtn} onClick={() => setShowAgentSheet(true)}>
              <img src="/svg/agent_spam_settings.svg" alt="agent_spam_settings" />
              Agent spam settings
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                style={{ marginLeft: "auto" }}
              >
                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Agent filter for blacklist */}
          <div className={styles.blAgentFilter}>
            <select
              className={styles.formInput2}
              value={blSelectedAgent}
              onChange={(e) => setBlSelectedAgent(e.target.value)}
            >
              <option value="all">All Agents</option>
              {agents.map((a) => (
                <option key={a.agent_id} value={a.agent_id}>{a.agentName || a.agent_id}</option>
              ))}
            </select>
          </div>

          {(() => {
            const agentFiltered = blSelectedAgent === "all" ? blacklist : blacklist.filter((b) => b.agentId === blSelectedAgent);
            const autoCount = agentFiltered.filter((b) => b.reason === "auto_spam").length;
            const manualCount = agentFiltered.filter((b) => b.reason === "manual" || b.reason === "reported").length;
            const totalCount = agentFiltered.length;
            return (
              <div className={styles.statsRow}>
                <div
                  className={styles.statBox}
                  onClick={() => setBlReasonFilter("auto_spam")}
                  style={{ cursor: "pointer", border: blReasonFilter === "auto_spam" ? "" : "2px solid transparent", borderRadius: 10 }}
                >
                  <span className={styles.statNum}>{autoCount}</span>
                  <span className={styles.statLabel}>Auto-blocked</span>
                </div>
                <div
                  className={styles.statBox}
                  onClick={() => setBlReasonFilter("manual")}
                  style={{ cursor: "pointer", border: blReasonFilter === "manual" ? "" : "2px solid transparent", borderRadius: 10 }}
                >
                  <span className={styles.statNum}>{manualCount}</span>
                  <span className={styles.statLabel}>Manual</span>
                </div>
                <div
                  className={styles.statBox}
                  onClick={() => setBlReasonFilter("total")}
                  style={{ cursor: "pointer", border: blReasonFilter === "total" ? "" : "2px solid transparent", borderRadius: 10 }}
                >
                  <span className={styles.statNum}>{totalCount}</span>
                  <span className={styles.statLabel}>Total</span>
                </div>
              </div>
            );
          })()}

          <div className={styles.listArea}>
            {(() => {
              let filtered = blSelectedAgent === "all" ? blacklist : blacklist.filter((b) => b.agentId === blSelectedAgent);
              if (blReasonFilter === "auto_spam") filtered = filtered.filter((b) => b.reason === "auto_spam");
              else if (blReasonFilter === "manual") filtered = filtered.filter((b) => b.reason === "manual" || b.reason === "reported");
              if (filtered.length === 0) {
                return (
                  <div className={styles.emptyState}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="#ccc" strokeWidth="1.5" />
                      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" stroke="#ccc" strokeWidth="1.5" />
                    </svg>
                    <p className={styles.emptyTitle}>No blocked numbers</p>
                    <p className={styles.emptySub}>Spam callers will appear here automatically</p>
                  </div>
                );
              }
              return filtered.map((entry) => {
                const agentName = agents.find((a) => a.agent_id === entry.agentId)?.agentName || "";
                return (
                  <AgentContactCard
                    key={entry.id}
                    readOnly={readOnly}
                    ac={{ ...entry, _agentName: agentName, _hideDelete: true, _blacklistMode: true }}
                    onUnblock={async (ac) => {
                      await onRemoveBlacklist?.(ac);
                      await loadBlacklist();
                      loadAgentContacts(acSelectedAgent);
                    }}
                  />
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* ── FAB ── */}
      {tab === "contacts" && !readOnly && (
        <>
          {/* Backdrop */}
          {fabOpen && <div className={styles.backdrop} onClick={() => setFabOpen(false)} />}
          <div className={styles.fabWrap} ref={fabRef}>
            {fabOpen && (
              <div className={styles.fabMenu}>
                {/* Sync writes into the account being viewed — only offered
                    when that account is the signed-in person's own. */}
                {canSync && (
                  <button
                    className={styles.fabItem}
                    onClick={() => {
                      setFabOpen(false);
                      setShowSync(true);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <polyline
                        points="23 4 23 10 17 10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    Sync Contacts
                  </button>
                )}
                <button
                  className={styles.fabItem}
                  onClick={() => {
                    setFabOpen(false);
                    setEditContact(null);
                    setShowAdd(true);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <line
                      x1="12"
                      y1="5"
                      x2="12"
                      y2="19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <line
                      x1="5"
                      y1="12"
                      x2="19"
                      y2="12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Add Contact
                </button>
                <button
                  className={styles.fabItem}
                  onClick={() => {
                    setFabOpen(false);
                    setShowUpload(true);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <polyline
                      points="17 8 12 3 7 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <line
                      x1="12"
                      y1="3"
                      x2="12"
                      y2="15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Upload File
                </button>
              </div>
            )}
            <button
              className={`${styles.fab} ${fabOpen ? styles.fabRotated : ""}`}
              onClick={() => setFabOpen((p) => !p)}
              data-tour="fab"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <line x1="12" y1="5" x2="12" y2="19" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="5" y1="12" x2="19" y2="12" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </>
      )}

      {/* Sync + Upload Dialogs rendered below via isOpen pattern */}

      {/* ══════════════════════════════════════════════
          ALL MODALS — controlled via isOpen boolean
          NewModal handles backdrop, animation, close btn
         ══════════════════════════════════════════════ */}

      <ForwardPhoneModal
        isOpen={showForwardModal}
        currentPhone={forwardPhone}
        onClose={() => setShowForwardModal(false)}
        onSave={handleForwardSave}
      />

      <AddCategoryModal
        isOpen={showAddCategory}
        onClose={() => setShowAddCategory(false)}
        activeCats={activeCats}
        agentId={agentId}
        onAdd={(key) => {
          const newCats = [...activeCats, key];
          setDynamicCategories(newCats);
          if (agentId) updateAgentCategories(agentId, { categories: newCats }).catch(console.error);
        }}
      />

      <NewModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        title="Advanced Filter"
        height="auto"
      >
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#888" }}>
          Filter contacts by type. Multiple selections are supported.
        </p>

        {/* All Contacts */}
        <div
          onClick={() => setAdvFilters({ all: true, vip: false, nonVip: false, blocked: false, nonBlocked: false })}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "11px 14px", borderRadius: 10, marginBottom: 12, cursor: "pointer",
            border: advFilters.all ? "1.5px solid #6524eb" : "1.5px solid #ebebf0",
            background: advFilters.all ? "#f3eeff" : "#fafafa",
          }}
        >
          <CustomCheckbox checked={advFilters.all} />
          <span style={{ fontSize: 14, fontWeight: 500, color: "#1a1a2e", flex: 1 }}>All Contacts</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#6524eb", background: "#ede5ff", padding: "2px 8px", borderRadius: 999 }}></span>
        </div>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "4px 0 12px" }}>
          <div style={{ flex: 1, height: 1, background: "#ebebf0" }} />
          <span style={{ fontSize: 11, color: "#aaa" }}>or choose specific</span>
          <div style={{ flex: 1, height: 1, background: "#ebebf0" }} />
        </div>

        {/* 2x2 Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {FILTER_OPTIONS.map(({ key, label }) => (
            <div
              key={key}
              onClick={() => setAdvFilters(prev => {
                const next = { ...prev, all: false, [key]: !prev[key] };
                if (!FILTER_OPTIONS.some(o => next[o.key])) next.all = true;
                return next;
              })}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "10px 12px", borderRadius: 10, cursor: "pointer",
                border: advFilters[key] ? "1.5px solid #6524eb" : "1.5px solid #ebebf0",
                background: advFilters[key] ? "#f3eeff" : "#fafafa",
              }}
            >
              <CustomCheckbox checked={advFilters[key]} />
              <span style={{ fontSize: 13, fontWeight: 500, color: "#1a1a2e" }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Active count */}
        {FILTER_OPTIONS.filter(o => advFilters[o.key]).length > 0 && (
          <p style={{ margin: "10px 0 0", fontSize: 12, color: "#6524eb", fontWeight: 500, textAlign: "center" }}>
            {FILTER_OPTIONS.filter(o => advFilters[o.key]).length} filter(s) selected
          </p>
        )}

        {/* Buttons */}
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button
            onClick={() => { setAdvFilters({ all: true, vip: false, nonVip: false, blocked: false, nonBlocked: false }); }}
            style={{ flex: 1, padding: "11px 0", borderRadius: 10, background: "#f5f5f7", border: "1.5px solid #ebebf0", fontSize: 14, fontWeight: 500, color: "#555", cursor: "pointer" }}
          >
            Clear
          </button>
          <button
            onClick={() => setShowFilterModal(false)}
            style={{ flex: 2, padding: "11px 0", borderRadius: 10, background: "#6524eb", border: "none", fontSize: 14, fontWeight: 600, color: "#fff", cursor: "pointer" }}
          >
            Apply Filter
            {FILTER_OPTIONS.filter(o => advFilters[o.key]).length > 0 && (
              <span style={{ marginLeft: 8, background: "rgba(255,255,255,0.25)", borderRadius: 999, fontSize: 11, padding: "1px 7px" }}>
                {FILTER_OPTIONS.filter(o => advFilters[o.key]).length}
              </span>
            )}
          </button>
        </div>
      </NewModal>


      <SyncDialog
        isOpen={showSync}
        onClose={() => {
          setShowSync(false);
          // Notify the onboarding wrapper (if any). Defensive
          // optional-call so dashboard mount — which doesn't pass this
          // prop — is unaffected.
          if (typeof onSyncDialogClose === "function") {
            onSyncDialogClose();
          }
        }}
        onSync={onSync || ((type, file) => console.log("Sync:", type, file))}
        onTriggerGoogleSync={onTriggerGoogleSync}
        oauthReturnTo={oauthReturnTo}
      />

      <UploadDialog
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        onSync={onSync}
        onImportDone={(res) => {
          if (!res) return;
          reloadContacts();
          loadAgentContacts(acSelectedAgent);
          const msg = [];
          if (res.inserted > 0) msg.push(`${res.inserted} new contact(s) added`);
          if (res.updated > 0) msg.push(`${res.updated} contact(s) already existed and were updated`);
          if (res.failed > 0) msg.push(`${res.failed} failed`);
          if (msg.length > 0) setImportResult(msg.join("\n"));
        }}
      />

      <ContactModal
        isOpen={showAdd}
        contact={editContact}
        categories={activeCats}
        onClose={() => {
          setShowAdd(false);
          setEditContact(null);
        }}
        onSave={async (form) => {
          try {
            if (editContact?._isPromote) {
              // Promote: save form data, then show confirm if blocked
              const promoteData = { name: form.name, email: form.email, company: form.company, category: form.category, isVip: form.isVip };
              const acId = editContact._agentContactId;
              const phone = editContact.phone;

              // Check if blocked on any agent BEFORE saving
              const acRecord = acList.find(a => a.id === acId);
              const blockedOnAny = acList.filter(a => a.phone === phone && a.isBlocked);

              if (blockedOnAny.length > 0) {
                // Show confirm FIRST, then save
                const agentNames = blockedOnAny.map(b => b._agentName || b.agentId).join(", ");
                setEditContact(null);
                setShowAdd(false);
                setConfirmState({
                  title: "Number is Blocked",
                  message: `This number is blocked on: ${agentNames}.\nSave to My Contacts and unblock all agents?`,
                  confirmLabel: "Unblock & Save",
                  cancelLabel: "Cancel",
                  onConfirm: async () => {
                    try {
                      const res = await apiPromoteAgentContact(acId, promoteData);
                      await apiUnblockAllAgents(phone);
                      const newContact = res?.contact || { id: Date.now(), ...form, isActive: true, source: "manual" };
                      addContactLocally(newContact);
                      loadAgentContacts(acSelectedAgent);
                      loadBlacklist();
                      showSuccess(`"${form.name || phone}" saved & unblocked!`);
                    } catch (e) { console.error(e); }
                    setConfirmState(null);
                  },
                });
                return;
              }

              // Not blocked — save directly
              const res = await apiPromoteAgentContact(acId, promoteData);
              const newContact = res?.contact || { id: Date.now(), ...form, isActive: true, source: "manual" };
              addContactLocally(newContact);
              loadAgentContacts(acSelectedAgent);
              reloadContacts();
              showSuccess(`"${form.name || form.phone}" moved to My Contacts!`);
            } else if (editContact) {
              // Check if phone was changed to an already-existing number
              if (form.phone && form.phone !== editContact.phone) {
                const existingContact = contacts.find(
                  (c) => c.id !== editContact.id && c.phone === form.phone
                );
                if (existingContact) {
                  setEditContact(null);
                  setShowAdd(false);
                  setConfirmState({
                    title: "Number Already Exists",
                    message: `"${existingContact.name || existingContact.phone}" already has this number in your contacts.\n\nDo you still want to update?`,
                    confirmLabel: "Update Anyway",
                    cancelLabel: "Cancel",
                    onConfirm: async () => {
                      try {
                        const res = await onUpdateContact?.(editContact.id, form);
                        const updated = res?.data || res || form;
                        updateSingleContactLocally(editContact.id, { ...updated, ...form });
                        showSuccess(`Contact "${form.name || form.phone}" updated`);
                      } catch (e) { console.error(e); }
                      setConfirmState(null);
                    },
                  });
                  return;
                }
              }
              const res = await onUpdateContact?.(editContact.id, form);
              const updated = res?.data || res || form;
              updateSingleContactLocally(editContact.id, { ...updated, ...form });
              showSuccess(`Contact "${form.name || form.phone}" updated`);
            } else {
              const res = await onCreateContact?.(form);
              if (res?.isDuplicate) {
                // Duplicate found — ask user what to do
                const dup = res.duplicateInfo;
                setEditContact(null);
                setShowAdd(false);
                setConfirmState({
                  title: "Number Already Exists",
                  message: `"${dup.existingName || dup.phone}" is already in your contacts${dup.source === "auto_call" ? " (auto-saved from calls)" : ""}.\n\nUpdate this contact with the new details?`,
                  confirmLabel: "Update",
                  cancelLabel: "Cancel",
                  onConfirm: async () => {
                    try {
                      await onCreateContact?.({ ...form, _forceCreate: true });
                      reloadContacts();
                      showSuccess(`"${form.name || form.phone}" updated`);
                    } catch (e) { console.error(e); }
                    setConfirmState(null);
                  },
                });
                return;
              }
              if (res?.existsInAgentContacts) {
                // Number exists in agent contacts (auto-saved from calls)
                const info = res.agentContactInfo;
                setEditContact(null);
                setShowAdd(false);
                setConfirmState({
                  title: "Number Found in Call History",
                  message: `This number has ${info.totalCalls} call(s) across ${info.agentCount} agent(s)${info.callerName ? ` as "${info.callerName}"` : ""}.${info.anyBlocked ? "\n\n⚠️ This number is blocked on some agents." : ""}\n\nSave to My Contacts? All agents will recognize this contact.`,
                  confirmLabel: "Save",
                  cancelLabel: "Cancel",
                  onConfirm: async () => {
                    try {
                      await onCreateContact?.({ ...form, _forceCreate: true });
                      reloadContacts();
                      loadAgentContacts(acSelectedAgent);
                      showSuccess(`"${form.name || form.phone}" saved to My Contacts`);
                    } catch (e) { console.error(e); }
                    setConfirmState(null);
                  },
                });
                return;
              }
              const newC = res?.contact || res?.data || res || {
                id: Date.now(),
                ...form,
                isActive: true,
              };
              addContactLocally(newC);
              showSuccess(`Contact "${form.name || form.phone}" added`);
            }
            setEditContact(null);
            setShowAdd(false);
          } catch (err) {
            return { error: err.message || "Failed to save contact" };
          }
        }}
      />

      <AgentSettingsSheet
        isOpen={showAgentSheet}
        agents={agents}
        returnTo={returnTo}
        onClose={() => setShowAgentSheet(false)}
        onSave={async (aid, settings) => {
          await onAgentSettingsSave?.(aid, settings);

          setAgentSpamStates((prev) => ({
            ...prev,
            [aid]: {
              ...prev[aid],
              ...settings
            }
          }));
        }}
        onLoad={onAgentSettingsLoad}
      />

      <ConfirmDialog
        isOpen={showSpamOffConfirm}
        title="Disable Auto Spam Blocking?"
        message={`Auto spam blocking will be turned off for all ${agents.length} agent(s). You can re-enable it anytime.`}
        confirmLabel="Yes, Disable"
        confirmDanger
        onCancel={() => setShowSpamOffConfirm(false)}
        onConfirm={disableAllSpam}
      />

      <ConfirmDialog
        isOpen={!!confirmState}
        title={confirmState?.title || ""}
        message={confirmState?.message || ""}
        confirmLabel={confirmState?.confirmLabel || "Confirm"}
        cancelLabel={confirmState?.cancelLabel || "Cancel"}
        confirmDanger={confirmState?.danger || false}
        onCancel={() => { if (confirmState?.onCancel) confirmState.onCancel(); else setConfirmState(null); }}
        onConfirm={confirmState?.onConfirm || (() => { })}
      />

      <AgentPickerDialog
        isOpen={showAgentPicker}
        agents={agents}
        onClose={() => setShowAgentPicker(false)}
        onConfirm={enableAgentSpam}
      />

      {/* Import Result Dialog */}
      <NewModal isOpen={!!importResult} onClose={() => setImportResult(null)} title="Import Complete" height="auto" maxHeight="50vh">
        <p style={{ margin: "0 0 20px", fontSize: 14, color: "#444", lineHeight: 1.6, whiteSpace: "pre-line" }}>{importResult}</p>
        <button
          onClick={() => setImportResult(null)}
          style={{ width: "100%", padding: "11px 0", background: "#6524eb", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
        >
          OK
        </button>
      </NewModal>
    </div>
  );
};

// ── Mock Data ─────────────────────────────────────────────────────────────────
const MOCK_AGENTS = [];
const MOCK_CONTACTS = [];
const MOCK_BLACKLIST = [];

export default ContactManager;
